import React, { useState, useEffect, useCallback } from 'react';
import { View, Text, StyleSheet, ScrollView, TouchableOpacity, Alert, TextInput, Platform } from 'react-native';

// React Native exposes `global` as the global scope, but the web TypeScript target
// doesn't type it.  This declaration satisfies the compiler in both environments.
declare const global: typeof globalThis;

import { getColor } from '../../config/appConfig';
import { appConfig } from '../../config/configManager';
import { parseServiceResponse } from '../../services/serviceHelpers';
import { createContentQuery } from '../../stores/contentStore';
import { useUserStore, useProfileStore } from '../../contexts/StoreContext';
import type { TableData, TablesDto } from '../../types/api/tables';
import { AdminSegmentStorage } from '../../services/AdminSegmentStorage';
import { AdminAssessmentStorage } from '../../services/AdminAssessmentStorage';
import type { AdminAssessmentDto } from '../../types/adminAssessment';
import { Base64ImageStorage } from '../../storage/Base64ImageStorage';
import { ActionDefStorage } from '../../storage/ActionDefStorage';
import { AppLabelsStorage } from '../../storage/AppLabelsStorage';
import { ActivitiesDefStorage } from '../../storage/ActivitiesDefStorage';
import { GuidanceDefStorage } from '../../storage/GuidanceDefStorage';
import { WellCheckStorage } from '../../storage/WellCheckStorage';
import { SkinTypeDefStorage } from '../../storage/SkinTypeDefStorage';
import { testMilestoneReload } from '../../utils/testMilestoneReload';
import { createMasterMilestoneStorage } from '../../storage/masterMilestoneStorage';
import { ConfigManager } from '../../config/configManager';
import { API_CONSTANTS } from '../../config/constants';
import { ContentBundleGenerator, BundleConfigManager, REQUIRED_CONTENT_CONFIG } from '../../utils/contentBundler';
import { BundledContentInitializer } from '../../utils/platformBundleLoaders';
import type { ServiceRequestDto } from '../../types/api/service';
import type { AdminStateDto } from '../../types/adminState';
import type { ChildProfile, MilestoneState, ConcernState } from '../../types/profile';
import { AdminStateStorage } from '../../services/AdminStateStorage';
import { AdminResponseStorage } from '../../storage/AdminResponseStorage';
import type { AdminResponseDto } from '../../types/AdminResponseDto';
import { CONCERN_STORAGE_KEY, StoredConcernData } from '../../types/concerns';
import { AccountUserStorage } from '../../storage/AccountUserStorage';
import { AccountPracInfoStorage } from '../../storage/AccountPracInfoStorage';
import type { AccountGetInfoResponseDto, AccountReferCompleteDto, AccountReferCompleteResponseDto } from '../../types/api/service';
import type { AccountPracInfoRequestDto, AccountPracInfoType } from '../../types/api';
import { AccountPracInfoService, normalizeAccountPracInfoPayload } from '../../services/account';
import { SETTING_NAME_TO_STORAGE_KEY, PREF_STORAGE_DEFAULTS, applyUserSettingsFromDto } from '../../utils/applyUserSettings';

const getRuntimeStorage = () =>
  (globalThis as any).__proGroupsStorage ?? (globalThis as any).__childNavStorage ?? (global as any).__proGroupsStorage ?? (global as any).__childNavStorage ?? null;

// Profile List DTOs matching C# backend
interface ProfileGetListResponseDto {
  profiles: ProfileGetListItemDto[];
}

interface ProfileGetListItemDto {
  profileId: string;
  modified: string; // DateTime as ISO string
}

// Enhanced login response interface
interface LoginResponseDataDto {
  token: string;
  refreshToken?: string;
  expiresAt?: string;
  user: any;
  profiles?: ProfileGetListResponseDto;
}

// Date normalization utility functions
const normalizeDate = (dateString: string): Date => {
  // Handle server dates without timezone info (assume UTC)
  if (dateString && !dateString.includes('Z') && !dateString.includes('+')) {
    // Add 'Z' to indicate UTC for consistent parsing
    return new Date(dateString + 'Z');
  }
  return new Date(dateString);
};

// Check if sync is needed considering date formats and 2-minute threshold
const shouldSyncProfile = (serverModified: string, localSyncDate: string | undefined | null): boolean => {
  if (!localSyncDate) return true; // No local sync date means we should sync
  
  const serverDate = normalizeDate(serverModified);
  const localDate = normalizeDate(localSyncDate);
  
  // 2-minute threshold in milliseconds
  const SYNC_THRESHOLD_MS = 2 * 60 * 1000;
  const timeDifference = serverDate.getTime() - localDate.getTime();
  
  console.log(`   📅 Date comparison for profile:`);
  console.log(`     Server: ${serverModified} -> ${serverDate.toISOString()}`);
  console.log(`     Local:  ${localSyncDate} -> ${localDate.toISOString()}`);
  console.log(`     Diff:   ${Math.round(timeDifference / 1000)}s (threshold: 120s)`);
  
  return timeDifference > SYNC_THRESHOLD_MS;
};

const ACCOUNT_PRAC_INFO_TYPES: AccountPracInfoType[] = [
  'Subscriptions',
  'AccountSettings',
  'Touchpoints',
  'AdminOptions',
  'AccountSras',
];

// Profile sync utility functions
const convertAppProfileDtoToChildProfile = (appProfile: any): ChildProfile => {
  // Convert MVarData arrays back to milestone/concern states
  const milestonestate: MilestoneState[] = appProfile.milestones.map((m: any) => ({
    mvarid: m.name,
    state: m.value === '1' ? 'yes' : m.value === '2' ? 'sometimes' : 'not-yet' as 'yes' | 'sometimes' | 'not-yet',
    lastUpdated: new Date().toISOString(),
    notes: ''
  }));
  
  const concernsstate: ConcernState[] = appProfile.concerns.map((c: any) => ({
    mvarid: c.name,
    state: c.value === '1' ? 'yes' : 'no' as 'yes' | 'no',
    lastUpdated: new Date().toISOString(),
    notes: ''
  }));
  
  // Extract development data from admin records
  const devage: { communication: number | null; motor: number | null; finemotor: number | null; grossmotor: number | null; cognitive: number | null; socialemotional: number | null } = { communication: null, motor: null, finemotor: null, grossmotor: null, cognitive: null, socialemotional: null };
  const devfloor: { communication: number | null; motor: number | null; finemotor: number | null; grossmotor: number | null; cognitive: number | null; socialemotional: number | null } = { communication: null, motor: null, finemotor: null, grossmotor: null, cognitive: null, socialemotional: null };
  const devceiling: { communication: number | null; motor: number | null; finemotor: number | null; grossmotor: number | null; cognitive: number | null; socialemotional: number | null } = { communication: null, motor: null, finemotor: null, grossmotor: null, cognitive: null, socialemotional: null };
  const devdelay: { communication: number | null; motor: number | null; finemotor: number | null; grossmotor: number | null; cognitive: number | null; socialemotional: number | null } = { communication: null, motor: null, finemotor: null, grossmotor: null, cognitive: null, socialemotional: null };
  const devresultscore: { communication: number | null; motor: number | null; finemotor: number | null; grossmotor: number | null; cognitive: number | null; socialemotional: number | null } = { communication: null, motor: null, finemotor: null, grossmotor: null, cognitive: null, socialemotional: null };
  
  // Process admin data to reconstruct development tracking
  appProfile.adminstates.forEach((admin: any) => {
    admin.data.forEach((item: any) => {
      if (item.name.startsWith('devAge_')) {
        const domain = item.name.replace('devAge_', '') as keyof typeof devage;
        if (domain in devage) {
          devage[domain] = parseFloat(item.value) || null;
        }
      } else if (item.name.startsWith('devFloor_')) {
        const domain = item.name.replace('devFloor_', '') as keyof typeof devfloor;
        if (domain in devfloor) {
          devfloor[domain] = parseFloat(item.value) || null;
        }
      } else if (item.name.startsWith('devCeiling_')) {
        const domain = item.name.replace('devCeiling_', '') as keyof typeof devceiling;
        if (domain in devceiling) {
          devceiling[domain] = parseFloat(item.value) || null;
        }
      } else if (item.name.startsWith('devDelay_')) {
        const domain = item.name.replace('devDelay_', '') as keyof typeof devdelay;
        if (domain in devdelay) {
          devdelay[domain] = parseFloat(item.value) || null;
        }
      } else if (item.name.startsWith('devResultScore_')) {
        const domain = item.name.replace('devResultScore_', '') as keyof typeof devresultscore;
        if (domain in devresultscore) {
          devresultscore[domain] = parseFloat(item.value) || null;
        }
      }
    });
  });
  
  // Extract extra PHI data
  const agephysical = parseFloat(appProfile.phi.extra.find((e: any) => e.name === 'agephysical')?.value || '0') || 0;
  const isPremium = appProfile.phi.extra.find((e: any) => e.name === 'isPremium')?.value === '1';
  
  return {
    id: appProfile.profileId,
    name: appProfile.phi.dname || appProfile.phi.fname || 'Unknown',
    email: appProfile.phi.email || undefined,
    accountnum: appProfile.accountId,
    dob: appProfile.phi.dob,
    agephysical,
    devage,
    devfloor,
    devceiling,
    devfloorCeilingTimestamp: {
      communication: new Date().toISOString(),
      motor: new Date().toISOString(),
      finemotor: new Date().toISOString(),
      grossmotor: new Date().toISOString(),
      cognitive: new Date().toISOString(),
      socialemotional: new Date().toISOString(),
    },
    devdelay,
    devresult: { communication: null, motor: null, finemotor: null, grossmotor: null, cognitive: null, socialemotional: null },
    devresultscore,
    devresultcomputed: { communication: null, motor: null, finemotor: null, grossmotor: null, cognitive: null, socialemotional: null },
    devstate: { communication: 0, motor: 0, finemotor: 0, grossmotor: 0, cognitive: 0, socialemotional: 0 },
    gender: (appProfile.phi.gender as any) || 'prefer-not-to-say',
    milestonestate,
    concernsstate,
    isPremium,
    createdDate: new Date().toISOString(),
    lastActiveDate: new Date().toISOString(),
    lastSyncDate: appProfile.modified,
    isDirty: false
  };
};

const syncSingleProfile = async (
  profileId: string,
  apiService: any,
  storage: any,
  profileStore: any,
  accountId: string,
  userStore: any
): Promise<{ success: boolean; error?: string }> => {
  try {
    console.log(`🔄 Syncing profile: ${profileId}`);
    
    // Create profile_get request
    const request = {
      RequestType: 'profile_get',
      Payload: JSON.stringify({ profileId, accountId })
    };
    
    // Fetch profile from server with automatic 401 retry handling
    const response = await apiService.processServiceRequestWithRefresh(request, 'sync', {
      userStore,
      storage,
    });
    
    if (!response?.success) {
      throw new Error(`Server error: ${response?.message || 'Unknown error'}`);
    }
    
    // Parse the response payload (matches DebugView implementation)
    let appProfileDto;
    try {
      if (typeof response.payload === 'string') {
        appProfileDto = JSON.parse(response.payload);
      } else {
        appProfileDto = response.payload;
      }
    } catch (parseError) {
      throw new Error(`Failed to parse profile data: ${parseError instanceof Error ? parseError.message : String(parseError)}`);
    }
    
    console.log(`   ✅ Fetched profile data for ${profileId}`);
    
    // Convert to ChildProfile format
    const childProfile = convertAppProfileDtoToChildProfile(appProfileDto);
    
    // Update profile store
    if (profileStore) {
      const profileStoreState = profileStore.getState();
      const existingProfile = profileStoreState.profiles.find((p: any) => p.id === profileId);
      
      if (existingProfile) {
        // Update existing profile
        profileStoreState.updateProfile(profileId, childProfile);
        console.log(`   🔄 Updated existing profile ${profileId}`);
      } else {
        // Add new profile - ensure we preserve the server's profile ID
        console.log(`   ➕ Adding new profile with server ID: ${profileId}`);
        
        // Add the profile with the server's ID
        if (typeof profileStoreState.addProfile === 'function') {
          await profileStoreState.addProfile(childProfile);
          console.log(`   ✅ Successfully added profile with ID: ${profileId}`);
        } else {
          console.error(`   ❌ addProfile method not available on profile store`);
        }
      }
    }
    
    return { success: true };
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : String(error);
    console.error(`   ❌ Failed to sync profile ${profileId}:`, errorMessage);
    return { success: false, error: errorMessage };
  }
};


interface DebugViewProps {
  onBack: () => void;
  storage?: any;
  apiService?: any;
  contentService?: any;
  useContentStore?: any;
  onSignOut?: () => void | Promise<void>;
}

export const DebugView: React.FC<DebugViewProps> = ({ 
  onBack, 
  storage, 
  apiService, 
  contentService, 
  useContentStore,
  onSignOut,
}) => {
  const config = appConfig.getConfig();
  
  // Access stores for authentication timing and profile data
  const userStore = useUserStore();
  const profileStoreHook = useProfileStore();
  const profileState = profileStoreHook(); // Get the state once at component level
  const { getActiveProfile } = profileState;

  // Subscribe reactively to user session changes so that getAccountStatus() always
  // reflects the current state — even after initializeApp/restoreSessionFromStorage restores the
  // session asynchronously after mount (without this, the header stays "❌ Not signed in"
  // until some other state change triggers a re-render).
  const [_sessionVersion, setSessionVersion] = useState(0);
  useEffect(() => {
    if (!userStore || typeof userStore === 'boolean' || typeof (userStore as any).subscribe !== 'function') return;
    const unsubscribe = (userStore as any).subscribe(
      (state: any) => state.session?.isAuthenticated,
      () => setSessionVersion(v => v + 1),
    );
    return unsubscribe;
  }, [userStore]);

  // Helper function to calculate minutes since a timestamp
  const getMinutesSince = (timestamp: string): number => {
    const now = new Date().getTime();
    const then = new Date(timestamp).getTime();
    return Math.floor((now - then) / (1000 * 60));
  };

  // Clear all stored auth credentials and sign out of the user store session.
  // Called when credentials are detected as invalid (401) so AccountView no longer
  // shows "Account Active" and the user can re-enter credentials.
  const clearAuthState = async () => {
    console.log('🔐 [clearAuthState] Clearing stored auth credentials...');
    const keysToRemove = [
      'auth_token', 'refresh_token', 'auth_timestamp',
      'user_email', 'user_name', 'user_password', 'user_signature',
      'userSession',
    ];
    if (storage) {
      for (const key of keysToRemove) {
        try { await storage.removeItem(key); } catch { /* ignore */ }
      }
    }
    try {
      if (userStore && typeof (userStore as any).getState === 'function') {
        const state = (userStore as any).getState();
        if (typeof state.logout === 'function') {
          await state.logout();
          console.log('🔐 [clearAuthState] userStore.logout() called');
        }
      }
    } catch (e) {
      console.warn('🔐 [clearAuthState] logout error:', e);
    }
    if (apiService && typeof apiService.setAuthToken === 'function') {
      apiService.setAuthToken('');
    }
    console.log('🔐 [clearAuthState] Done — auth state cleared');
  };

  // State for debug operations
  const [isLoading, setIsLoading] = useState(false);
  const [contentEntries, setContentEntries] = useState<any[]>([]);
  const [assessmentEntries, setAssessmentEntries] = useState<any[]>([]);
  const [assessmentResponses, setAssessmentResponses] = useState<AdminResponseDto[]>([]);
  const [lastOperation, setLastOperation] = useState<string>('');
  const [showDebugInfo, setShowDebugInfo] = useState(false);
  const [adminStatesClearedAt, setAdminStatesClearedAt] = useState<number | null>(null);
  const [enableEditPageLogic, setEnableEditPageLogic] = useState(false);
  const [enableEditCalcLogic, setEnableEditCalcLogic] = useState(false);
  const [enableEditReportLogic, setEnableEditReportLogic] = useState(false);

  // Development assessment configuration state
  const [devInitWindow, setDevInitWindow] = useState(config.development.devInitWindow);
  const [devYesRequired, setDevYesRequired] = useState(config.development.devYesRequired);
  const [devNotYetRequired, setDevNotYetRequired] = useState(config.development.devNotYetRequired);

  // UI feature flags state
  const [enableChangeAccount, setEnableChangeAccount] = useState(config.features.enableChangeAccount);
  const [enableLastUpdate, setEnableLastUpdate] = useState(false); // Debug setting for showing last update timestamps
  // Settings View > Preferences feature enables (all off by default)
  const [enablePushNotifications, setEnablePushNotifications] = useState(false);
  const [enableDarkMode, setEnableDarkMode] = useState(false);
  const [enableAutoBackup, setEnableAutoBackup] = useState(false);
  const [enableAnalytics, setEnableAnalytics] = useState(false);
  const [jwtTokenTimestamp, setJwtTokenTimestamp] = useState<string | null>(null);

  // Custom content request parameters
  const [customContentType, setCustomContentType] = useState('AdminSegment');
  const [customContentId, setCustomContentId] = useState('1987');
  const [customLanguageId, setCustomLanguageId] = useState('1');
  const [debugPremiumMode, setDebugPremiumMode] = useState<boolean | null>(null); // null = use default
  const [profileList, setProfileList] = useState<ProfileGetListItemDto[]>([]);

  // AccountGetInfo state
  const [accountGetInfo, setAccountGetInfo] = useState<AccountGetInfoResponseDto | null>(null);
  const [accountGetInfoSavedAt, setAccountGetInfoSavedAt] = useState<string | null>(null);

  // Referral qualification debug state
  const [isDebugReferring, setIsDebugReferring] = useState(false);
  const [debugReferResult, setDebugReferResult] = useState<string | null>(null);

  // Segment label generator state (Report Script Tools)
  const [segLabelSegmentId, setSegLabelSegmentId] = useState('11272');
  const [segLabelVarName, setSegLabelVarName] = useState('segmentLabels');
  const [segLabelOutput, setSegLabelOutput] = useState('');
  const [isGeneratingLabels, setIsGeneratingLabels] = useState(false);
  const [segNodeOrderOutput, setSegNodeOrderOutput] = useState('');
  const [isGeneratingNodeOrder, setIsGeneratingNodeOrder] = useState(false);
  const [segAnswerLabelsOutput, setSegAnswerLabelsOutput] = useState('');
  const [isGeneratingAnswerLabels, setIsGeneratingAnswerLabels] = useState(false);

  // Content type options for dropdown
  const contentTypeOptions = [
    { label: 'AdminSegment', value: 'AdminSegment' },
    { label: 'AdminAssessment', value: 'AdminAssessment' },
    { label: 'Base64Image', value: 'Base64Image' },
    { label: 'WellCheckDef', value: 'WellCheckDef' },
    { label: 'SkinTypeDef', value: 'SkinTypeDef' },
    { label: 'AppLabels', value: 'AppLabels' }
  ];

  // Handle content type selection
  const handleContentTypeSelection = () => {
    Alert.alert(
      'Select Content Type',
      'Choose the content type to download:',
      [
        ...contentTypeOptions.map((option) => ({
          text: option.label,
          onPress: () => setCustomContentType(option.value),
        })),
        {
          text: 'Cancel',
          style: 'cancel' as const,
        },
      ]
    );
  };

  useEffect(() => {
    console.log('🔎 DebugView Custom Download contentTypeOptions:', contentTypeOptions.map((option) => `${option.label}:${option.value}`));
  }, []);

  // Load debug toggle state from storage
  useEffect(() => {
    const loadDebugState = async () => {
      if (!storage) return;
      try {
        const debugState = await storage.getItem('debug_show_info');
        if (debugState === 'true') {
          setShowDebugInfo(true);
        }
        
        // Load premium mode override
        const premiumOverride = await storage.getItem('debug_premium_mode');
        if (premiumOverride === 'standard') {
          setDebugPremiumMode(false);
          ConfigManager.setDebugPremiumOverride(false);
        } else if (premiumOverride === 'premium') {
          setDebugPremiumMode(true);
          ConfigManager.setDebugPremiumOverride(true);
        } else {
          setDebugPremiumMode(null); // Use default
          ConfigManager.setDebugPremiumOverride(null);
        }
        
        // Load edit page logic state
        const editPageLogicState = await storage.getItem('debug_edit_page_logic');
        if (editPageLogicState === 'true') {
          setEnableEditPageLogic(true);
        }
        
        // Load edit calc logic state
        const editCalcLogicState = await storage.getItem('debug_edit_calc_logic');
        if (editCalcLogicState === 'true') {
          setEnableEditCalcLogic(true);
        }
        
        // Load edit report logic state
        const editReportLogicState = await storage.getItem('debug_edit_report_logic');
        if (editReportLogicState === 'true') {
          setEnableEditReportLogic(true);
        }
        
        // Load last update display setting
        const lastUpdateSetting = await storage.getItem('debug_show_last_update');
        if (lastUpdateSetting === 'true') {
          setEnableLastUpdate(true);
        }
        
        // Load enable change account setting
        const changeAccountSetting = await storage.getItem('debug_enable_change_account');
        if (changeAccountSetting === 'true') {
          setEnableChangeAccount(true);
        }

        // Load Settings View Preferences feature enables
        const pushNotifSetting = await storage.getItem('debug_enable_push_notifications');
        if (pushNotifSetting === 'true') setEnablePushNotifications(true);
        const darkModeSetting = await storage.getItem('debug_enable_dark_mode');
        if (darkModeSetting === 'true') setEnableDarkMode(true);
        const autoBackupSetting = await storage.getItem('debug_enable_auto_backup');
        if (autoBackupSetting === 'true') setEnableAutoBackup(true);
        const analyticsSetting = await storage.getItem('debug_enable_analytics');
        if (analyticsSetting === 'true') setEnableAnalytics(true);
        
        // Load JWT token timestamp for duration tracking
        const tokenTimestamp = await storage.getItem('auth_timestamp');
        setJwtTokenTimestamp(tokenTimestamp);

        // Auto-load AccountGetInfo from storage so Referral Qualification section
        // shows correct values without requiring a manual button press.
        try {
          const userState = (typeof userStore !== 'boolean' && (userStore as any).getState)
            ? (userStore as any).getState()
            : null;
          const autoAccountId =
            userState?.session?.user?.familyId ||
            userState?.session?.familyAccount?.familyId ||
            '';
          if (autoAccountId) {
            const autoAccountUserStorage = new AccountUserStorage(storage);
            const autoDto = await autoAccountUserStorage.getAccountGetInfo(autoAccountId);
            if (autoDto) {
              setAccountGetInfo(autoDto);
              setAccountGetInfoSavedAt((autoDto as any)._savedAt || null);
            }
          }
        } catch { /* non-fatal */ }
      } catch (error) {
        console.error('Failed to load debug state:', error);
      }
    };
    loadDebugState();
  }, [storage, userStore]);

  // Periodically update JWT token timestamp for accurate duration display
  useEffect(() => {
    const updateTokenTimestamp = async () => {
      if (storage) {
        const tokenTimestamp = await storage.getItem('auth_timestamp');
        setJwtTokenTimestamp(tokenTimestamp);
      }
    };

    // Update immediately and then every 30 seconds
    updateTokenTimestamp();
    const interval = setInterval(updateTokenTimestamp, 30000);
    
    return () => clearInterval(interval);
  }, [storage]);

  // Get session info for Development Tools section
  const getUserSessionInfo = () => {
    try {
      if (userStore && typeof userStore !== 'boolean' && userStore.getState) {
        const state = userStore.getState();
        const session = state?.session;
        
        if (session && session.isAuthenticated && session.sessionStartTime) {
          const loginMinutes = getMinutesSince(session.sessionStartTime);
          const activityMinutes = session.lastActivityTime ? getMinutesSince(session.lastActivityTime) : null;
          
          if (activityMinutes !== null) {
            return `Login: ${loginMinutes}m ago, Activity: ${activityMinutes}m ago`;
          } else {
            return `Login: ${loginMinutes}m ago`;
          }
        }
      }
      return 'No active session found';
    } catch (error) {
      console.error('Error getting session info:', error);
      return 'Error accessing session info';
    }
  };

  // Get account status for Account Tools header
  const getAccountStatus = () => {
    try {
      if (userStore && typeof userStore !== 'boolean' && userStore.getState) {
        const state = userStore.getState();
        const session = state?.session;
        const localProfiles = profileStoreHook?.getState?.()?.profiles?.length || 0;
        
        if (session && session.isAuthenticated) {
          const loginMinutes = session.sessionStartTime ? getMinutesSince(session.sessionStartTime) : 0;
          const tokenMinutes = jwtTokenTimestamp ? getMinutesSince(jwtTokenTimestamp) : null;
          const userEmail = session.user?.email || 'Unknown';
          const serverProfiles = profileList.length;
          
          // Format token duration info
          const tokenInfo = tokenMinutes !== null ? `• JWT:${tokenMinutes}m` : '';
          
          return `✅ ${userEmail} • Login:${loginMinutes}m ${tokenInfo} • ${localProfiles}L/${serverProfiles}S`;
        } else {
          return `❌ Not signed in • ${localProfiles} local profiles`;
        }
      }
      return '❓ Status unknown';
    } catch (error) {
      console.error('Error getting account status:', error);
      return '⚠️ Status error';
    }
  };

  // Save debug toggle state to storage
  const toggleDebugInfo = async () => {
    const newState = !showDebugInfo;
    setShowDebugInfo(newState);
    
    if (storage) {
      try {
        await storage.setItem('debug_show_info', newState.toString());
        console.log('🐛 Debug state saved:', newState);
      } catch (error) {
        console.error('Failed to save debug state:', error);
      }
    }
  };

  // Save edit page logic toggle state to storage
  const toggleEditPageLogic = async () => {
    const newState = !enableEditPageLogic;
    setEnableEditPageLogic(newState);
    
    if (storage) {
      try {
        await storage.setItem('debug_edit_page_logic', newState.toString());
        console.log('🔧 Edit Page Logic state saved:', newState);
      } catch (error) {
        console.error('Failed to save edit page logic state:', error);
      }
    }
  };

  // Save edit calc logic toggle state to storage
  const toggleEditCalcLogic = async () => {
    const newState = !enableEditCalcLogic;
    setEnableEditCalcLogic(newState);
    
    if (storage) {
      try {
        await storage.setItem('debug_edit_calc_logic', newState.toString());
        console.log('🔧 Edit Calc Logic state saved:', newState);
      } catch (error) {
        console.error('Failed to save edit calc logic state:', error);
      }
    }
  };

  // Save edit report logic toggle state to storage
  const toggleEditReportLogic = async () => {
    const newState = !enableEditReportLogic;
    setEnableEditReportLogic(newState);
    
    if (storage) {
      try {
        await storage.setItem('debug_edit_report_logic', newState.toString());
        console.log('🔧 Edit Report Logic state saved:', newState);
      } catch (error) {
        console.error('Failed to save edit report logic state:', error);
      }
    }
  };

  // Toggle premium mode for debugging
  const togglePremiumMode = async () => {
    let newMode: boolean | null;
    if (debugPremiumMode === null) {
      // Default -> Premium
      newMode = true;
    } else if (debugPremiumMode === true) {
      // Premium -> Standard
      newMode = false;
    } else {
      // Standard -> Default
      newMode = null;
    }
    
    setDebugPremiumMode(newMode);
    ConfigManager.setDebugPremiumOverride(newMode);
    
    if (storage) {
      try {
        const modeString = newMode === null ? 'default' : (newMode ? 'premium' : 'standard');
        await storage.setItem('debug_premium_mode', modeString);
        console.log('🏆 Premium mode debug override saved:', modeString);
        setLastOperation(`⚙️ Premium mode set to: ${modeString}`);
      } catch (error) {
        console.error('Failed to save premium mode debug state:', error);
        setLastOperation(`❌ Failed to save premium mode: ${error}`);
      }
    }
  };

  // Send current profile via apiService profile_put (delegates to syncProfileToBackend)
  const handlePutCurrentProfile = async () => {
    try {
      setIsLoading(true);
      setLastOperation('Syncing current profile to backend...');

      const activeProfile = getActiveProfile();

      if (!activeProfile) {
        setLastOperation('❌ No active profile found');
        if (Platform.OS === 'web') {
          (globalThis as any).alert?.('Error\n\nNo active profile to sync');
        } else {
          Alert.alert('Error', 'No active profile to sync');
        }
        return;
      }

      // Use the same syncProfileToBackend mechanism as dirty-profile sync
      const success = await profileStoreHook.getState().syncProfileToBackend(activeProfile.id);

      if (success) {
        setLastOperation(`✅ Profile sync successful for: ${activeProfile.name}`);
        if (Platform.OS === 'web') {
          (globalThis as any).alert?.(`Success\n\nProfile "${activeProfile.name}" synced to backend successfully!`);
        } else {
          Alert.alert('Success', `Profile "${activeProfile.name}" synced to backend successfully!`);
        }
      } else {
        setLastOperation(`❌ Profile sync failed — check console for details`);
        if (Platform.OS === 'web') {
          (globalThis as any).alert?.(`Error\n\nProfile sync failed. Check console for details.`);
        } else {
          Alert.alert('Error', 'Profile sync failed. Check console for details.');
        }
      }

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      setLastOperation(`❌ Profile sync error: ${errorMessage}`);
      if (Platform.OS === 'web') {
        (globalThis as any).alert?.(`Error\n\nProfile sync failed: ${errorMessage}`);
      } else {
        Alert.alert('Error', `Profile sync failed: ${errorMessage}`);
      }
      console.error('Profile sync error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Clear current profile data
  const handleClearCurrentProfile = async () => {
    try {
      setIsLoading(true);
      setLastOperation('Clearing current profile data...');

      const activeProfile = getActiveProfile();
      
      if (!activeProfile) {
        setLastOperation('❌ No active profile found');
        if (Platform.OS === 'web') {
          (globalThis as any).alert?.('Error\n\nNo active profile to clear');
        } else {
          Alert.alert('Error', 'No active profile to clear');
        }
        return;
      }

      console.log('🗑️ Clearing profile data for:', activeProfile.name);
      
      // Clear AdminState data from AdminStateStorage
      try {
        const storage = getRuntimeStorage();
        if (storage) {
          const adminStateStorage = new AdminStateStorage(storage);
          
          console.log('🗑️ Clearing AdminState data for profile...');
          
          // Get all AdminState objects for this profile
          const allAdminStates = await adminStateStorage.queryAdminStates({
            profileId: activeProfile.id
          });
          
          console.log(`🗑️ Found ${allAdminStates.length} AdminState objects to clear`);
          
          // Delete each AdminState object
          for (const adminState of allAdminStates) {
            try {
              await adminStateStorage.deleteAdminState(
                adminState.profileId,
                adminState.sourceId,
                adminState.recId
              );
              console.log(`🗑️ Deleted AdminState: ${adminState.sourceId} (${adminState.status})`);
            } catch (deleteError) {
              console.warn(`⚠️ Failed to delete AdminState ${adminState.sourceId}:`, deleteError);
            }
          }
          
          // Clear AdminStateStorage cache
          adminStateStorage.clearCache();
          console.log('🗑️ Cleared AdminStateStorage cache');
          
          // Set timestamp to prevent reloading AdminStates immediately
          setAdminStatesClearedAt(Date.now());
        } else {
          console.warn('⚠️ No storage available for AdminState cleanup');
        }
      } catch (adminStateError) {
        console.warn('⚠️ Error clearing AdminState data:', adminStateError);
        // Continue with profile clearing even if AdminState cleanup fails
      }

      // Clear milestone and concern states, plus any domain/admin state arrays
      const updatedProfile = {
        ...activeProfile,
        milestonestate: [],
        concernsstate: [],
        // Clear domain state arrays if they exist
        ...(activeProfile.hasOwnProperty('domainstate') && { domainstate: [] }),
        ...(activeProfile.hasOwnProperty('adminstates') && { adminstates: [] }),
        devage: {
          communication: null,
          motor: null,
          finemotor: null,
          grossmotor: null,
          cognitive: null,
          socialemotional: null,
        },
        devfloor: {
          communication: 0,
          motor: 0,
          finemotor: 0,
          grossmotor: 0,
          cognitive: 0,
          socialemotional: 0,
        },
        devceiling: {
          communication: 0,
          motor: 0,
          finemotor: 0,
          grossmotor: 0,
          cognitive: 0,
          socialemotional: 0,
        },
        devdelay: {
          communication: 0,
          motor: 0,
          finemotor: 0,
          grossmotor: 0,
          cognitive: 0,
          socialemotional: 0,
        },
        devresultscore: {
          communication: 0,
          motor: 0,
          finemotor: 0,
          grossmotor: 0,
          cognitive: 0,
          socialemotional: 0,
        },
        devstate: {
          communication: 0,
          motor: 0,
          finemotor: 0,
          grossmotor: 0,
          cognitive: 0,
          socialemotional: 0,
        },
        // Reset development result computed timestamps if they exist
        ...(activeProfile.hasOwnProperty('devresultcomputed') && {
          devresultcomputed: {
            communication: null,
            motor: null,
            finemotor: null,
            grossmotor: null,
            cognitive: null,
            socialemotional: null,
          }
        })
      };

      // Update profile in store using the pre-obtained state
      if (profileState && profileState.updateProfile) {
        profileState.updateProfile(activeProfile.id, updatedProfile);
      }

      setLastOperation(`✅ Profile data cleared for: ${activeProfile.name}`);
      if (Platform.OS === 'web') {
        (globalThis as any).alert?.(`Success\n\nProfile data cleared for "${activeProfile.name}" - including AdminState assessments from all storage locations`);
      } else {
        Alert.alert('Success', `Profile data cleared for "${activeProfile.name}" - including AdminState assessments from all storage locations`);
      }

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      setLastOperation(`❌ Clear profile error: ${errorMessage}`);
      if (Platform.OS === 'web') {
        (globalThis as any).alert?.(`Error\n\nFailed to clear profile: ${errorMessage}`);
      } else {
        Alert.alert('Error', `Failed to clear profile: ${errorMessage}`);
      }
      console.error('Clear profile error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Get current profile from backend using common userStore pattern
  const handleGetCurrentProfile = async () => {
    try {
      setIsLoading(true);
      setLastOperation('Getting current profile from backend...');

      const activeProfile = getActiveProfile();
      
      if (!activeProfile) {
        setLastOperation('❌ No active profile found');
        if (Platform.OS === 'web') {
          (globalThis as any).alert?.('Error\n\nNo active profile to get');
        } else {
          Alert.alert('Error', 'No active profile to get');
        }
        return;
      }

      const userState = (typeof userStore !== 'boolean' && userStore.getState) ? userStore.getState() : null;
      const accountId = userState?.session?.familyAccount?.familyId || 'debug-account';
      
      console.log('📥 Getting profile from backend:', {
        profileId: activeProfile.id,
        accountId: accountId,
        profileName: activeProfile.name
      });

      // Create profile_get request
      const request: ServiceRequestDto = {
        RequestType: 'profile_get',
        Payload: JSON.stringify({
          profileId: activeProfile.id,
          accountId: accountId
        })
      };

      setLastOperation('Sending profile_get request to backend...');
      
      if (!apiService) {
        setLastOperation('❌ API service not available');
        if (Platform.OS === 'web') {
          (globalThis as any).alert?.('Error\n\nAPI service not available');
        } else {
          Alert.alert('Error', 'API service not available');
        }
        return;
      }

      // Send via apiService with automatic 401 retry handling
      const response = await apiService.processServiceRequestWithRefresh(request, 'sync', {
        userStore,
        storage,
      });
      
      // Handle response using common pattern from userStore
      if (response?.success && response?.payload) {
        console.log('📥 Received successful response from backend:', {
          success: response.success,
          payloadType: typeof response.payload,
          payloadLength: response.payload?.length
        });
        
        // Parse the payload directly as it contains profile data JSON.
        let appProfile: any;
        try {
          if (typeof response.payload === 'string') {
            appProfile = JSON.parse(response.payload);
          } else {
            appProfile = response.payload as any;
          }
          
          // Normalise PascalCase 'Actions' that some backend builds may return
          if (!(appProfile as any).actions && (appProfile as any).Actions) {
            (appProfile as any).actions = (appProfile as any).Actions;
          }
          console.log('📥 Successfully parsed profile payload from backend:', {
            profileId: appProfile.profileId,
            milestonesCount: appProfile.milestones.length,
            concernsCount: appProfile.concerns.length,
            actionsCount: appProfile.actions?.length ?? 0,
            adminstatesCount: appProfile.adminstates.length,
            name: appProfile.phi.dname
          });
        } catch (parseError) {
          console.error('📥 Failed to parse profile payload:', {
            error: parseError,
            payloadType: typeof response.payload,
            payloadPreview: response.payload?.toString().substring(0, 100)
          });
          setLastOperation(`❌ Profile get failed: Failed to parse response payload`);
          if (Platform.OS === 'web') {
            (globalThis as any).alert?.('Error\n\nProfile get failed: Failed to parse response payload');
          } else {
            Alert.alert('Error', `Profile get failed: Failed to parse response payload`);
          }
          return;
        }
        
        const convertedProfile = convertAppProfileDtoToChildProfile(appProfile);
        
        // Merge converted data with existing profile, preserving ID and local settings.
        // If the backend returned no actions (profile_get may omit the actions array),
        // keep the existing local actionsstate rather than wiping it out.
        const serverActionsCount = appProfile.actions?.length ?? 0;
        const preservedActionsState = serverActionsCount > 0
          ? convertedProfile.actionsstate
          : activeProfile.actionsstate;
        const updatedProfile = {
          ...convertedProfile,
          id: activeProfile.id, // Keep existing ID
          lastSyncDate: appProfile.modified || new Date().toISOString(),
          isDirty: false,
          syncFailureReason: undefined,
          actionsstate: preservedActionsState,
        };
        console.log(`📥 Actions: backend returned ${serverActionsCount}, local had ${activeProfile.actionsstate?.length ?? 0}, using ${updatedProfile.actionsstate?.length ?? 0}`);

        console.log('🔄 Converted profile using common userStore pattern:', updatedProfile);
        
        // Update profile in store
        if (profileState && profileState.updateProfile) {
          profileState.updateProfile(activeProfile.id, updatedProfile);
        }

        const actionsNote = serverActionsCount > 0
          ? `${serverActionsCount} actions from server`
          : `0 actions from server — kept ${updatedProfile.actionsstate?.length ?? 0} local action(s)`;
        setLastOperation(`✅ Profile data updated from backend for: ${updatedProfile.name} (${actionsNote})`);
        if (Platform.OS === 'web') {
          (globalThis as any).alert?.(`Success\n\nProfile "${updatedProfile.name}" updated from backend data!\n\nReceived:\n• ${appProfile.milestones?.length || 0} milestones\n• ${appProfile.concerns?.length || 0} concerns\n• ${appProfile.adminstates?.length || 0} admin state records\n• ${actionsNote}\n• PHI data (name, email, DOB, etc.)\n• Development data (devAge, devFloor, devCeiling, etc.)\n\nUsed common sync pattern from userStore.`);
        } else {
          Alert.alert(
            'Success', 
            `Profile "${updatedProfile.name}" updated from backend data!\n\nReceived:\n• ${appProfile.milestones?.length || 0} milestones\n• ${appProfile.concerns?.length || 0} concerns\n• ${appProfile.adminstates?.length || 0} admin state records\n• ${actionsNote}\n• PHI data (name, email, DOB, etc.)\n• Development data (devAge, devFloor, devCeiling, etc.)\n\nUsed common sync pattern from userStore.`
          );
        }
        
      } else {
        setLastOperation(`❌ Profile get failed: ${response?.message || 'No data received'}`);
        if (Platform.OS === 'web') {
          (globalThis as any).alert?.(`Error\n\nProfile get failed: ${response?.message || 'No data received'}`);
        } else {
          Alert.alert('Error', `Profile get failed: ${response?.message || 'No data received'}`);
        }
      }

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      setLastOperation(`❌ Profile get error: ${errorMessage}`);
      if (Platform.OS === 'web') {
        (globalThis as any).alert?.(`Error\n\nProfile get failed: ${errorMessage}`);
      } else {
        Alert.alert('Error', `Profile get failed: ${errorMessage}`);
      }
      console.error('Profile get error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const loadContentEntries = useCallback((contentTypeFilter?: string) => {
    console.log('🔍 DebugView: Loading content entries...', {
      contentTypeFilter,
      useContentStoreAvailable: !!useContentStore
    });
    
    if (useContentStore) {
      try {
        const state = useContentStore.getState();
        console.log(`   ContentStore state: ${!!state}`);
        
        // Get raw records first to avoid expensive processing of all records
        const rawRecords = state?.records || [];
        console.log(`   📋 Found ${rawRecords.length} total records in store`);
        
        // Filter raw records by contentType BEFORE expensive field extraction
        let recordsToProcess = rawRecords;
        if (contentTypeFilter) {
          recordsToProcess = rawRecords.filter((record: any) => 
            record.contentType === contentTypeFilter
          );
          console.log(`   🎯 Pre-filtered to ${recordsToProcess.length} ${contentTypeFilter} records (avoiding ${rawRecords.length - recordsToProcess.length} unnecessary extractions)`);
        } else {
          console.log(`   📋 Processing all ${rawRecords.length} records with basic info only (no field extraction)`);
        }
        
        // Extract fields only when contentTypeFilter is specified
        const recordsWithFields = recordsToProcess.map((record: any) => {
          // If no content type filter, return basic record info without field extraction
          // BUT for AdminSegment entries, we still need to extract SurveybId/SurveyAliasId for display
          if (!contentTypeFilter) {
            let displayTitle = record.contentTypeId || record.id || 'Unknown';
            
            // For AdminSegment entries, try to extract IDs for better display
            if (record.contentType === 'AdminSegment' && record.tableData) {
              console.log('🔍 loadContentEntries: Processing AdminSegment for display extraction:', record.contentTypeId);
              try {
                const tableData = record.tableData;
                console.log('🔍 tableData available:', !!tableData, 'rows:', tableData?.Data?.Rows?.length);
                
                if (tableData?.Data?.Rows && tableData.Data.Rows.length > 0) {
                  console.log('🔍 Available columns:', tableData.Data.Columns);
                  const bodyColumnIndex = tableData.Data.Columns.findIndex((col: string) => 
                    col.toLowerCase() === 'content_body'
                  );
                  console.log('🔍 content_body column index:', bodyColumnIndex);
                  
                  if (bodyColumnIndex !== -1) {
                    const contentBody = tableData.Data.Rows[0][bodyColumnIndex];
                    console.log('🔍 contentBody found:', !!contentBody, 'length:', contentBody?.length);
                    
                    if (contentBody) {
                      const decodedContent = atob(contentBody);
                      console.log('🔍 Decoded base64 content (first 200 chars):', decodedContent.substring(0, 200));
                      
                      // Skip parse if content appears truncated (incomplete JSON)
                      const trimmedContent = decodedContent.trim();
                      if (!trimmedContent.endsWith('}') && !trimmedContent.endsWith(']')) {
                        console.log('🔍 AdminSegment content appears truncated (length:', decodedContent.length, '), skipping parse');
                        displayTitle = `${record.contentTypeId || 'N/A'} (truncated)`;
                      } else {
                        const adminSegmentDto = JSON.parse(trimmedContent);
                        
                        console.log('🔍 Raw AdminSegmentDto from backend:', adminSegmentDto);
                        console.log('🔍 AdminSegmentDto keys:', Object.keys(adminSegmentDto));
                        console.log('🔍 SurveybId field:', adminSegmentDto.SurveybId, 'type:', typeof adminSegmentDto.SurveybId);
                        console.log('🔍 SurveyAliasId field:', adminSegmentDto.SurveyAliasId, 'type:', typeof adminSegmentDto.SurveyAliasId);
                        
                        const surveybId = adminSegmentDto.SurveybId || record.contentTypeId || 'N/A';
                        const surveyAliasId = adminSegmentDto.SurveyAliasId || 'N/A';
                        
                        console.log('🔍 loadContentEntries extracted:', {
                          surveybId,
                          surveyAliasId,
                          title: adminSegmentDto.Title
                        });
                        
                        // Create displayTitle with both IDs if SurveyAliasId is different
                        if (surveyAliasId !== 'N/A' && surveyAliasId !== surveybId) {
                          displayTitle = `${surveybId} (Alias: ${surveyAliasId})`;
                        } else {
                          displayTitle = surveybId;
                        }
                        
                        console.log('🔍 Final displayTitle for AdminSegment:', displayTitle);
                      }
                    } else {
                      console.warn('🔍 contentBody is empty for AdminSegment');
                    }
                  } else {
                    console.warn('🔍 content_body column not found in AdminSegment');
                  }
                } else {
                  console.warn('🔍 No tableData rows available for AdminSegment');
                }
              } catch (error) {
                console.log('Failed to parse AdminSegment IDs for display:', error);
                displayTitle = record.contentTypeId || record.id || 'Unknown';
              }
            }
            
            return {
              ...record,
              name: displayTitle,
              title: displayTitle,
              languageId: record.languageTypeId || '1'
            };
          }

          // Only do expensive field extraction when filtering for specific content type
          try {
            // Ensure we have a valid record ID
            const recordId = record.id || record.contentId || `${record.contentType}_${record.contentTypeId}_${record.languageTypeId}`;
            
            if (!recordId || !record.tableData) {
              console.warn(`   ⚠️ Invalid record structure for:`, record);
              return {
                ...record,
                name: record.contentTypeId || 'Unknown',
                title: record.contentTypeId || 'Unknown',
                languageId: record.languageTypeId || '1'
              };
            }
            
            // Extract content fields for this specific record - pass the full record object
            const contentFields = state?.getContentTableFields ? 
              state.getContentTableFields(record) : null;
            
            // Handle null return from getContentTableFields
            const safeContentFields = contentFields || {};
            
            return {
              ...record,
              ...safeContentFields,
              // Ensure we have the basic fields even if extraction fails
              name: safeContentFields.content_title || record.contentTypeId || record.id || 'Unknown',
              title: safeContentFields.content_title || record.contentTypeId || record.id || 'Unknown',
              languageId: safeContentFields.content_languagetype_id || record.languageTypeId || '1'
            };
          } catch (error) {
            console.warn(`   ⚠️ Failed to extract fields for ${record.id || record.contentTypeId || 'unknown record'}:`, error);
            return {
              ...record,
              name: record.contentTypeId || record.id || 'Unknown',
              title: record.contentTypeId || record.id || 'Unknown',
              languageId: record.languageTypeId || '1'
            };
          }
        });
        
        // Log final processed records for debugging
        console.log('🔍 Final processed records:', recordsWithFields.map((r: any) => ({
          contentType: r.contentType,
          contentTypeId: r.contentTypeId,
          name: r.name,
          title: r.title,
          id: r.id
        })));
        
        setContentEntries(recordsWithFields);
        setLastOperation(`Loaded ${recordsWithFields.length} content entries from store${contentTypeFilter ? ` (${contentTypeFilter} only)` : ''}`);
        
        // Calculate field extractions saved based on filtering approach
        const fieldExtractionsPerformed = contentTypeFilter ? recordsToProcess.length : 0;
        const fieldExtractionsSaved = rawRecords.length - fieldExtractionsPerformed;
        console.log(`   ✅ Loaded ${recordsWithFields.length} content entries (${fieldExtractionsPerformed} field extractions performed, saved ${fieldExtractionsSaved} extractions)`);
      } catch (error) {
        console.error('   ❌ Error accessing content store:', error);
        setContentEntries([]);
        setLastOperation(`Error accessing content store: ${error instanceof Error ? error.message : String(error)}`);
      }
    } else {
      setContentEntries([]);
      setLastOperation('Content store not available');
      console.log('   ❌ Content store not available');
    }
  }, [useContentStore]);

  // Assessment Management Functions
  const loadAssessmentEntries = useCallback(async () => {
    console.log('🎯 DebugView: Loading assessment entries...');
    
    if (!storage) {
      console.log('   ⚠️ Storage not available');
      setAssessmentEntries([]);
      return;
    }
    
    try {
      const adminAssessmentStorage = new AdminAssessmentStorage(storage);
      const assessments = await adminAssessmentStorage.getAvailableAssessments();
      
      console.log(`   📋 Found ${assessments.length} assessments`);
      const formattedEntries = assessments.map((assessment, index) => ({
        id: `${assessment.sourceId}_${index}`,
        dName: assessment.dName,
        sourceId: assessment.sourceId,
        surveyId: assessment.surveyId,
        surveybList: assessment.surveybList,
        languageTypeId: assessment.languageTypeId,
        reportsCount: assessment.reports?.length || 0
      }));
      
      setAssessmentEntries(formattedEntries);
      console.log('   ✅ Assessment entries loaded successfully');
    } catch (error) {
      console.error('   ❌ Error loading assessment entries:', error);
      setAssessmentEntries([]);
    }
  }, [storage]);

  const loadAssessmentResponses = useCallback(async () => {
    console.log('📊 DebugView: Loading completed assessment responses...');

    if (!storage) {
      console.log('   ⚠️ Storage not available');
      setAssessmentResponses([]);
      return;
    }

    try {
      const activeProfile = getActiveProfile();
      if (!activeProfile) {
        console.log('   ⚠️ No active profile — cannot load responses');
        setAssessmentResponses([]);
        return;
      }

      const adminResponseStorage = new AdminResponseStorage(storage);
      const storageKey = `admin_responses_${activeProfile.id}`;
      console.log(`   🔑 Reading from storage key: ${storageKey}`);
      const profileResponses = await adminResponseStorage.getProfileResponses(activeProfile.id);
      const sorted = [...profileResponses.responses].sort((a, b) =>
        new Date(b.recUTM || b.recDate || 0).getTime() - new Date(a.recUTM || a.recDate || 0).getTime()
      );
      setAssessmentResponses(sorted);
      console.log(`   📊 Found ${sorted.length} completed response(s) for profile ${activeProfile.id}`);
    } catch (error) {
      console.error('   ❌ Error loading assessment responses:', error);
      setAssessmentResponses([]);
    }
  }, [storage, getActiveProfile]);

  const clearAssessmentResponses = useCallback(async () => {
    console.log('🗑️ DebugView: Clearing completed assessment responses...');
    if (!storage) return;
    try {
      const activeProfile = getActiveProfile();
      if (!activeProfile) return;
      const adminResponseStorage = new AdminResponseStorage(storage);
      const storageKey = `admin_responses_${activeProfile.id}`;
      await storage.removeItem(storageKey);
      setAssessmentResponses([]);
      console.log(`   ✅ Cleared responses for profile ${activeProfile.id}`);
    } catch (error) {
      console.error('   ❌ Error clearing assessment responses:', error);
    }
  }, [storage, getActiveProfile]);

  useEffect(() => {
    console.log('🔧 DebugView: Component mounted, props:', {
      storage: !!storage,
      apiService: !!apiService,
      contentService: !!contentService,
      useContentStore: !!useContentStore,
      useContentStoreType: typeof useContentStore
    });
    
    loadContentEntries();
    loadAssessmentEntries();
    loadAssessmentResponses();
    
  }, [loadContentEntries, loadAssessmentEntries, loadAssessmentResponses]);

  const handleClearAllAssessments = () => {
    const doClearAllAssessments = async () => {
      console.log('🗑️ DebugView: Attempting to clear all assessments...');

      if (storage) {
        try {
          setIsLoading(true);
          setLastOperation('🗑️ Clearing all assessment data...');

          const adminAssessmentStorage = new AdminAssessmentStorage(storage);
          await adminAssessmentStorage.clearAll();

          setLastOperation('✅ All assessment data cleared');
          if (Platform.OS === 'web') {
            (globalThis as any).alert?.('Success\n\nAll assessment data has been cleared from storage');
          } else {
            Alert.alert('Success', 'All assessment data has been cleared from storage');
          }
          console.log('   ✅ All assessments cleared successfully');

          // Reload assessment entries to show empty list
          loadAssessmentEntries();
        } catch (error) {
          console.error('   ❌ Error clearing assessments:', error);
          setLastOperation(`❌ Error clearing assessments: ${error instanceof Error ? error.message : String(error)}`);
          if (Platform.OS === 'web') {
            (globalThis as any).alert?.(`Error\n\nFailed to clear assessments: ${error instanceof Error ? error.message : String(error)}`);
          } else {
            Alert.alert('Error', `Failed to clear assessments: ${error instanceof Error ? error.message : String(error)}`);
          }
        } finally {
          setIsLoading(false);
        }
      } else {
        setLastOperation('❌ Storage not available');
        if (Platform.OS === 'web') {
          (globalThis as any).alert?.('Error\n\nStorage not available');
        } else {
          Alert.alert('Error', 'Storage not available');
        }
      }
    };

    if (Platform.OS === 'web') {
      if ((globalThis as any).confirm?.('Clear All Assessments\n\nThis will remove all assessment definitions from storage. This action cannot be undone. Continue?') !== false) {
        doClearAllAssessments();
      }
    } else {
      Alert.alert(
        'Clear All Assessments',
        'This will remove all assessment definitions from storage. This action cannot be undone. Continue?',
        [
          { text: 'Cancel', style: 'cancel' },
          { text: 'Clear All', style: 'destructive', onPress: doClearAllAssessments }
        ]
      );
    }
  };

  // Content Bundle Management Functions

  // AdminSegment-specific download function (more targeted)
  const handleDownloadAdminSegments = async () => {
    if (!contentService) {
      Alert.alert('Error', 'Content sync service not available');
      return;
    }

    setIsLoading(true);
    setLastOperation('Downloading AdminSegment content only...');
    
    try {
      const bundleGenerator = new ContentBundleGenerator(storage, useContentStore, contentService);
      
      // Get bundle config to find AdminSegments to download
      const bundleConfigManager = bundleGenerator.getBundleConfigManager();
      const config = bundleConfigManager.getBundleConfigDetails();
      
      console.log(`📊 Downloading ${config.adminSegments.length} AdminSegments only...`);
      
      // Download only AdminSegments
      const downloadPromises = config.adminSegments.map(async (segmentId: string) => {
        try {
          // Request AdminSegment content from backend
          const request = {
            RequestType: 'content_get',
            Payload: JSON.stringify({
              contentType: 'AdminSegment',
              contentTypeId: segmentId,
              languageTypeId: '1'
            })
          };
          
          console.log(`📥 Requesting AdminSegment ${segmentId}...`);
          const response = await contentService.requestContent(request);
          
          if (response?.success && response?.content) {
            console.log(`✅ Downloaded AdminSegment ${segmentId}`);
            return { segmentId, success: true };
          } else {
            console.warn(`⚠️ Failed to download AdminSegment ${segmentId}: ${response?.message}`);
            return { segmentId, success: false, error: response?.message };
          }
        } catch (error) {
          console.error(`❌ Error downloading AdminSegment ${segmentId}:`, error);
          return { segmentId, success: false, error: error instanceof Error ? error.message : String(error) };
        }
      });
      
      const results = await Promise.all(downloadPromises);
      const successful = results.filter(r => r.success).length;
      const failed = results.filter(r => !r.success);
      
      if (failed.length > 0) {
        console.warn('⚠️ Some AdminSegment downloads failed:', failed);
        setLastOperation(`⚠️ Downloaded ${successful}/${results.length} AdminSegments (${failed.length} failed)`);
        Alert.alert(
          'Partial Success', 
          `Downloaded ${successful} out of ${results.length} AdminSegments.\n\n${failed.length} failed downloads - check console for details.`
        );
      } else {
        setLastOperation(`✅ Downloaded all ${successful} AdminSegments successfully`);
        Alert.alert('Success', `All ${successful} AdminSegments downloaded successfully.`);
      }
      
      // Refresh content list - show only AdminSegments
      loadContentEntries('AdminSegment');
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      setLastOperation(`❌ AdminSegment download failed: ${errorMessage}`);
      Alert.alert('Error', `Failed to download AdminSegments: ${errorMessage}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleGenerateContentBundle = async () => {
    if (!storage || !useContentStore) {
      Alert.alert('Error', 'Storage or content store not available');
      return;
    }

    setIsLoading(true);
    setLastOperation('Generating content bundle...');
    
    try {
      const bundleGenerator = new ContentBundleGenerator(storage, useContentStore);
      const bundle = await bundleGenerator.generateBundle();
      
      // Save to storage for testing
      await bundleGenerator.saveBundleToStorage(bundle);
      
      // Export as JSON for deployment
      const bundleJson = bundleGenerator.exportBundleAsJSON(bundle);
      
      const successMsg =
        `Content bundle created with ${bundle.metadata.totalRecords} records.\n\n` +
        `Content Types: ${bundle.metadata.contentTypes.join(', ')}\n\n` +
        `The bundle JSON is ready for deployment. Check console for export data.`;

      setLastOperation(`✅ Content bundle generated with ${bundle.metadata.totalRecords} records`);
      console.log('📦 BUNDLE JSON FOR DEPLOYMENT:\n', bundleJson);

      if (Platform.OS === 'web') {
        (globalThis as any).alert?.(`Bundle Generated\n\n${successMsg}\n\nBundle JSON has been logged to the console.`);
      } else {
        Alert.alert(
          'Bundle Generated',
          successMsg,
          [
            { text: 'OK' },
            {
              text: 'Copy JSON',
              onPress: () => {
                console.log('📦 BUNDLE JSON FOR DEPLOYMENT:\n', bundleJson);
                Alert.alert('Bundle JSON', 'Bundle JSON has been logged to console. Copy and save as bundled-content.json in your assets folder.');
              }
            }
          ]
        );
      }
      
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      setLastOperation(`❌ Bundle generation failed: ${errorMessage}`);
      Alert.alert('Error', `Failed to generate content bundle: ${errorMessage}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleTestBundledContent = async () => {
    if (!storage || !useContentStore) {
      Alert.alert('Error', 'Storage or content store not available');
      return;
    }

    setIsLoading(true);
    setLastOperation('Testing bundled content loading...');
    
    try {
      const initializer = new BundledContentInitializer(storage, useContentStore);
      
      // Always force-load from bundled assets, overwriting any existing content
      await initializer.initialize();
      setLastOperation('✅ Bundled content loaded successfully');
      
      // Refresh content list to show loaded content
      loadContentEntries();

      // Extract any ActivityDef records from the bundle into ActivitiesDefStorage
      try {
        const contentStoreState = useContentStore.getState();
        const allRecords: any[] = contentStoreState?.records || [];
        const activityDefRecords = allRecords.filter((r: any) => r.contentType === 'ActivityDef');
        if (activityDefRecords.length > 0) {
          const globalStorage = getRuntimeStorage() || storage;
          const activitiesDefStorage = new ActivitiesDefStorage(globalStorage);
          let extractedCount = 0;
          for (const record of activityDefRecords) {
            const dto = await activitiesDefStorage.unpackFromContentRecord(record);
            if (dto) extractedCount++;
          }
          if (extractedCount > 0) {
            console.log(`✅ Extracted ${extractedCount} ActivityDef record(s) from bundle into ActivitiesDefStorage`);
          }
        }
      } catch (extractError) {
        console.warn('⚠️ ActivityDef extraction from bundle failed:', extractError);
      }

      // Extract any GuidanceDef records from the bundle into GuidanceDefStorage
      try {
        const contentStoreState = useContentStore.getState();
        const allRecords: any[] = contentStoreState?.records || [];
        const guidanceDefRecords = allRecords.filter((r: any) => r.contentType === 'GuidanceDef');
        if (guidanceDefRecords.length > 0) {
          const globalStorage = getRuntimeStorage() || storage;
          const guidanceDefStorage = new GuidanceDefStorage(globalStorage);
          let extractedCount = 0;
          for (const record of guidanceDefRecords) {
            const dto = await guidanceDefStorage.unpackFromContentRecord(record);
            if (dto) extractedCount++;
          }
          if (extractedCount > 0) {
            console.log(`✅ Extracted ${extractedCount} GuidanceDef record(s) from bundle into GuidanceDefStorage`);
          }
        }
      } catch (extractError) {
        console.warn('⚠️ GuidanceDef extraction from bundle failed:', extractError);
      }

      // Extract any skintypedef records from the bundle into SkinTypeDefStorage
      try {
        const contentStoreState = useContentStore.getState();
        const allRecords: any[] = contentStoreState?.records || [];
        const skinTypeDefRecords = allRecords.filter((r: any) => (r.contentType || '').toLowerCase() === 'skintypedef');
        if (skinTypeDefRecords.length > 0) {
          const globalStorage = getRuntimeStorage() || storage;
          const skinTypeDefStorage = new SkinTypeDefStorage(globalStorage);
          let extractedCount = 0;
          for (const record of skinTypeDefRecords) {
            const dto = await skinTypeDefStorage.unpackFromContentRecord(record);
            if (dto) extractedCount++;
          }
          if (extractedCount > 0) {
            console.log(`✅ Extracted ${extractedCount} skintypedef record(s) from bundle into SkinTypeDefStorage`);
          }
        }
      } catch (extractError) {
        console.warn('⚠️ skintypedef extraction from bundle failed:', extractError);
      }

      if (Platform.OS === 'web') {
        (globalThis as any).alert?.('Success\n\nBundled content has been loaded successfully. Check the content list to see loaded records.');
      } else {
        Alert.alert('Success', 'Bundled content has been loaded successfully. Check the content list to see loaded records.');
      }
      
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      setLastOperation(`❌ Bundled content test failed: ${errorMessage}`);
      Alert.alert('Error', `Failed to test bundled content: ${errorMessage}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleTestRefreshAuth = async () => {
    // ── Step 0: immediate proof the handler fired ──────────────────────────
    console.log('🔐 [RefreshAuth] ① Handler fired');
    setIsLoading(true);
    setLastOperation('Testing authentication refresh...');
    
    try {
      // ── Step 1: validate userStore ──────────────────────────────────────
      console.log('🔐 [RefreshAuth] ② Checking userStore:', {
        hasUserStore: !!userStore,
        userStoreType: typeof userStore,
        hasGetState: !!(userStore && typeof (userStore as any).getState === 'function'),
        hasSubscribe: !!(userStore && typeof (userStore as any).subscribe === 'function'),
      });

      if (!userStore || typeof (userStore as any).getState !== 'function') {
        const msg = `userStore is not available or has no getState (type: ${typeof userStore}). Cannot run RefreshAuth.`;
        console.error('🔐 [RefreshAuth] ✗', msg);
        setLastOperation(`❌ ${msg}`);
        if (Platform.OS === 'web') {
          (globalThis as any).alert?.(`RefreshAuth Error\n\n${msg}`);
        } else {
          Alert.alert('RefreshAuth Error', msg);
        }
        return;
      }

      const userState = userStore.getState();
      console.log('🔐 [RefreshAuth] ③ Got userState:', {
        hasUserState: !!userState,
        hasRestoreSessionFromStorage: !!(userState && typeof userState.restoreSessionFromStorage === 'function'),
        isAuthenticated: userState?.session?.isAuthenticated,
        hasUser: !!userState?.session?.user,
        email: userState?.session?.user?.email,
        hasAuthToken: !!(userState?.session?.user?.authToken),
      });

      if (!userState || typeof userState.restoreSessionFromStorage !== 'function') {
        const msg = `userState.restoreSessionFromStorage is not a function (has keys: ${userState ? Object.keys(userState).slice(0, 10).join(', ') : 'null'})`;
        console.error('🔐 [RefreshAuth] ✗', msg);
        setLastOperation(`❌ ${msg}`);
        if (Platform.OS === 'web') {
          (globalThis as any).alert?.(`RefreshAuth Error\n\n${msg}`);
        } else {
          Alert.alert('RefreshAuth Error', msg);
        }
        return;
      }

      // ── Step 2: check storage tokens before calling restoreSessionFromStorage ────────
      const storedRefreshToken = await storage?.getItem('refresh_token');
      const storedAuthToken    = await storage?.getItem('auth_token');
      const authTimestamp      = await storage?.getItem('auth_timestamp');
      const userSessionRaw     = await storage?.getItem('userSession');
      const userSessionParsed  = userSessionRaw ? (() => { try { return JSON.parse(userSessionRaw); } catch { return null; } })() : null;
      const sessionRefreshToken = userState?.session?.user?.refreshToken
        || userSessionParsed?.user?.refreshToken
        || userSessionParsed?.refreshToken
        || null;
      const effectiveRefreshToken = storedRefreshToken || sessionRefreshToken;

      if (!storedRefreshToken && sessionRefreshToken && storage?.setItem) {
        await storage.setItem('refresh_token', sessionRefreshToken);
      }

      console.log('🔐 [RefreshAuth] ④ Storage tokens:', {
        hasRefreshToken:  !!storedRefreshToken,
        hasSessionRefreshToken: !!sessionRefreshToken,
        hasEffectiveRefreshToken: !!effectiveRefreshToken,
        hasAuthToken:     !!storedAuthToken,
        authTimestamp,
        hasUserSession:   !!userSessionRaw,
        userSessionEmail: userSessionParsed?.user?.email ?? userSessionParsed?.email ?? '(not found)',
        apiServiceAvailable: !!apiService,
      });

      if (!effectiveRefreshToken) {
        const msg = 'No refresh token found in storage or session. Complete a credential sign-in flow that returns refreshToken, then retry.';
        console.warn('🔐 [RefreshAuth] ✗', msg);
        setLastOperation(`❌ ${msg}`);
        if (Platform.OS === 'web') {
          (globalThis as any).alert?.(`RefreshAuth Error\n\n${msg}`);
        } else {
          Alert.alert('RefreshAuth Error', msg);
        }
        return;
      }

      // ── Step 3: call restoreSessionFromStorage (tries to restore in-memory session) ──
      console.log('🔐 [RefreshAuth] ⑤ Calling userState.restoreSessionFromStorage()...');
      const refreshResult = await userState.restoreSessionFromStorage();
      console.log('🔐 [RefreshAuth] ⑥ restoreSessionFromStorage() returned:', refreshResult,
        '(false = userSession missing from storage or parse failed)');
      
      // ── Step 4: check final session state ───────────────────────────────
      const finalState = userState.session;
      console.log('🔐 [RefreshAuth] ⑦ Final session state:', {
        isAuthenticated: finalState?.isAuthenticated,
        hasUser: !!finalState?.user,
        email: finalState?.user?.email,
        refreshResult,
        apiServiceAvailable: !!apiService,
      });

      // ── Step 5: attempt the refresh-token API call ───────────────────────
      // Proceed even if restoreSessionFromStorage() returned false — it fails when userSession
      // is absent (e.g. after a Vite restart without clearing site data).
      // We still have the refresh_token so we can hit the endpoint directly.
      if (apiService) {
        try {
          if (effectiveRefreshToken) {
            // Call the actual refresh token endpoint
            console.log('🔐 [RefreshAuth] ⑧ Calling /api/authentication/RefreshToken endpoint...');
            
            // Get fresh user info for SraId and AccessType.
            // currentUser may be null if restoreSessionFromStorage() failed (session not in memory),
            // so fall back to the userSession we already read from storage.
            const freshUserState = userStore.getState();
            const currentUser = freshUserState.session.user;
            const sessionUser = userSessionParsed?.user ?? userSessionParsed;
            
            // Debug user object to see what's available
            console.log('🔐 [RefreshAuth] ⑨ Current user object:', {
              hasUser: !!currentUser,
              userId: currentUser?.userId,
              email: currentUser?.email,
              fallbackFromStorage: !currentUser && !!sessionUser,
              storageUserId: sessionUser?.userId ?? sessionUser?.id,
              storageEmail: sessionUser?.email,
            });
            
            const sraId = currentUser?.userId
              || (currentUser as any)?.id
              || sessionUser?.userId
              || sessionUser?.id
              || '';
            const accessType = API_CONSTANTS.DEFAULT_ACCESS_TYPE;
            
            console.log('🔐 [RefreshAuth] ⑩ RefreshTokenRequestDto:', {
              refreshToken: '[PRESENT]',
              SraId: sraId,
              AccessType: accessType,
            });
            
            const refreshResponse = await apiService.refreshToken({
              refreshToken: effectiveRefreshToken,
              SraId: sraId,
              AccessType: accessType
            });
            
            console.log('🔐 [RefreshAuth] ⑪ refreshToken API raw response:', {
              success: refreshResponse?.success,
              hasToken: !!refreshResponse?.data?.token,
              hasRefreshToken: !!refreshResponse?.data?.refreshToken,
              expiresAt: refreshResponse?.data?.expiresAt,
              message: refreshResponse?.message,
              // Admin history diagnostics: log both casing variants so we can see what the backend returned
              adminHistoryCount: refreshResponse?.data?.adminHistory?.adminHistory?.length
                ?? refreshResponse?.data?.adminHistory?.adminhistory?.length
                ?? refreshResponse?.data?.adminhistory?.adminHistory?.length
                ?? refreshResponse?.data?.adminhistory?.adminhistory?.length
                ?? 0,
              profilesCount: refreshResponse?.data?.profiles?.profiles?.length ?? 0,
            });

            // Process the refresh response using common logic (like ApiService does)
            console.log('🔐 [RefreshAuth] ⑫ Calling debugForceRefresh...');
            const userStoreState = userStore.getState ? userStore.getState() : userStore;
            const refreshSuccess = await apiService.debugForceRefresh(
              refreshResponse,
              userStore,
              storage
            );
            
            console.log('🔐 [RefreshAuth] ⑬ debugForceRefresh returned:', refreshSuccess);
            
            if (refreshSuccess && refreshResponse?.data?.token) {
              const newToken = refreshResponse.data.token;
              const newRefreshToken = refreshResponse.data.refreshToken;
              const expiresAt = refreshResponse.data.expiresAt;
              
              console.log('🔐 [RefreshAuth] ⑭ processRefreshResponse completed — tokens updated');
              
              // Update JWT token timestamp state for immediate display update
              const newTimestamp = await storage.getItem('auth_timestamp');
              setJwtTokenTimestamp(newTimestamp);
              
              // Update any additional services not handled by processRefreshResponse 
              if (contentService) {
                try {
                  if (contentService.setAuthToken) {
                    contentService.setAuthToken(newToken);
                    console.log('🔐 [RefreshAuth] ✅ Updated contentService with new token');
                  } else if (contentService.updateAuthToken) {
                    contentService.updateAuthToken(newToken);
                    console.log('🔐 [RefreshAuth] ✅ Updated contentService via updateAuthToken');
                  }
                } catch (syncError) {
                  console.warn('🔐 [RefreshAuth] ⚠️ Could not update contentService token:', syncError);
                }
              }
              
              console.log('🔐 [RefreshAuth] ⑮ Token refresh complete');

              // If restoreSessionFromStorage() failed earlier (userSession was missing), try it now.
              // processRefreshResponse only writes auth_token/refresh_token, not userSession,
              // so we need to write a minimal userSession before calling restoreSessionFromStorage().
              if (!refreshResult) {
                console.log('🔐 [RefreshAuth] ⑯ Re-running restoreSessionFromStorage() to restore in-memory session...');
                // Ensure userSession exists in storage so restoreSessionFromStorage() can read it back
                const existingSession = await storage.getItem('userSession');
                if (!existingSession) {
                  const userStoreSession = userStoreState?.session ?? (userStoreState as any)?.getState?.()?.session;
                  if (userStoreSession?.user) {
                    const minimalSession = {
                      user: { ...userStoreSession.user, authToken: newToken, refreshToken: newRefreshToken },
                      familyAccount: userStoreSession.familyAccount ?? null,
                      sessionStartTime: new Date().toISOString(),
                    };
                    await storage.setItem('userSession', JSON.stringify(minimalSession));
                    console.log('🔐 [RefreshAuth] ⑯a Wrote minimal userSession to storage for re-hydration');
                  } else {
                    console.warn('🔐 [RefreshAuth] ⑯a No in-memory user to write — re-hydration may still fail');
                  }
                }
                const rehydrated = await userState.restoreSessionFromStorage();
                console.log('🔐 [RefreshAuth] ⑰ Re-hydration result:', rehydrated);
              }

              // Refresh the assessment responses section now that sync has completed
              await loadAssessmentResponses();

              setLastOperation(`✅ RefreshAuth successful - Used common processRefreshResponse pattern`);

              // Count profiles and admin history items from the refresh response
              const refreshedProfilesCount =
                refreshResponse?.data?.profiles?.profiles?.length ??
                (Array.isArray(refreshResponse?.data?.profiles) ? (refreshResponse.data.profiles as any[]).length : 0);
              const refreshedAdminHistoryCount =
                refreshResponse?.data?.adminHistory?.adminHistory?.length ??
                refreshResponse?.data?.adminHistory?.adminhistory?.length ??
                refreshResponse?.data?.adminhistory?.adminHistory?.length ??
                refreshResponse?.data?.adminhistory?.adminhistory?.length ??
                0;

              const alertMessage = `Authentication refresh completed successfully!\n\n` +
                `• Auth Status: ${finalState.isAuthenticated ? 'Authenticated' : 'Not Authenticated'}\n` +
                `• User: ${finalState.user?.email || 'No user'}\n` +
                `• New JWT Token: ${newToken ? 'Received & Updated' : 'Missing'}\n` +
                `• New Refresh Token: ${newRefreshToken ? 'Received & Updated' : 'Missing'}\n` +
                `• Expires At: ${expiresAt || 'Not provided'}\n` +
                `• Profiles in response: ${refreshedProfilesCount}\n` +
                `• Admin history items: ${refreshedAdminHistoryCount}\n` +
                `• Backend Sync: Processed automatically by ApiService`;

              if (Platform.OS === 'web') {
                (globalThis as any).alert?.(`RefreshAuth Test Successful\n\n${alertMessage}`);
              } else {
                Alert.alert('RefreshAuth Test Successful', alertMessage);
              }
            } else {
              throw new Error(`Token refresh failed: ${refreshResponse?.message || 'processRefreshResponse returned false'}`);
            }
          } else {
            const errorMsg = `No stored refresh token found in storage. Use 'App Account Sign In' first.`;
            console.warn('🔐 [RefreshAuth] ⚠️ No refresh_token in storage');
            throw new Error(errorMsg);
          }
        } catch (apiError) {
          const apiErrMsg = apiError instanceof Error ? apiError.message : String(apiError);
          const is401 = apiErrMsg.includes('401') || apiErrMsg.toLowerCase().includes('unauthorized');
          console.error('🔐 [RefreshAuth] ✗ Refresh token API test failed:', apiError);
          await clearAuthState();
          if (is401) {
            setLastOperation(`❌ RefreshAuth failed (401 Unauthorized) — credentials cleared, please sign in again`);
            const msg = `Refresh token is invalid or expired (401 Unauthorized).\n\nYou have been signed out. Please sign in again with updated credentials.`;
            if (Platform.OS === 'web') {
              (globalThis as any).alert?.(`Session Invalid\n\n${msg}`);
            } else {
              Alert.alert('Session Invalid', msg);
            }
          } else {
            setLastOperation(`⚠️ RefreshAuth failed — credentials cleared: ${apiErrMsg}`);
            const partialMsg = `Authentication refresh failed — you have been signed out.\n\n• API Error: ${apiErrMsg}`;
            if (Platform.OS === 'web') {
              (globalThis as any).alert?.(`RefreshAuth Failed\n\n${partialMsg}`);
            } else {
              Alert.alert('RefreshAuth Failed', partialMsg);
            }
          }
        }
      } else {
        // No apiService — cannot make the API call
        const reason = 'apiService is not available';
        console.warn(`🔐 [RefreshAuth] ⚠️ Skipping API call — ${reason}`);
        const skipMsg = 'apiService is not available — cannot call RefreshToken endpoint.';
        setLastOperation(`⚠️ RefreshAuth: ${skipMsg}`);
        if (Platform.OS === 'web') {
          (globalThis as any).alert?.(`RefreshAuth — Not Complete\n\n${skipMsg}`);
        } else {
          Alert.alert('RefreshAuth — Not Complete', skipMsg);
        }
      }
      
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      console.error('🔐 RefreshAuth test failed:', error);
      await clearAuthState();
      setLastOperation(`❌ RefreshAuth test failed — credentials cleared: ${errorMessage}`);
      if (Platform.OS === 'web') {
        (globalThis as any).alert?.(`RefreshAuth Test Failed\n\nYou have been signed out.\n\nError: ${errorMessage}`);
      } else {
        Alert.alert('RefreshAuth Test Failed', `You have been signed out.\n\nError: ${errorMessage}`);
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Bundle Configuration Management Functions
  const handleUpdateBundleConfigFromStore = async () => {
    if (!useContentStore || !storage) {
      Alert.alert('Error', 'Content store or storage not available');
      return;
    }

    const doUpdate = async () => {
      setIsLoading(true);
      setLastOperation('📋 Updating bundle config from Content Store...');

      try {
        const bundleGenerator = new ContentBundleGenerator(storage, useContentStore, contentService);
        const bundleConfigManager = bundleGenerator.getBundleConfigManager();

        await bundleConfigManager.updateConfigFromContentStore(useContentStore);

        const configDetails = bundleConfigManager.getBundleConfigDetails();
        setLastOperation(`✅ Bundle config updated: ${configDetails.totalItems} items (AdminSegments: ${configDetails.adminSegments.length}, Other: ${configDetails.otherContent.length})`);

        if (Platform.OS === 'web') {
          (globalThis as any).alert?.(`Bundle Config Updated\n\nBundle configuration has been updated with ${configDetails.totalItems} items from your Content Store.\n\nAdminSegments: ${configDetails.adminSegments.length}\nOther Content: ${configDetails.otherContent.length}`);
        } else {
          Alert.alert(
            'Bundle Config Updated',
            `Bundle configuration has been updated with ${configDetails.totalItems} items from your Content Store.\n\nAdminSegments: ${configDetails.adminSegments.length}\nOther Content: ${configDetails.otherContent.length}`
          );
        }
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : String(error);
        setLastOperation(`❌ Failed to update bundle config: ${errorMessage}`);
        Alert.alert('Error', `Failed to update bundle config: ${errorMessage}`);
      } finally {
        setIsLoading(false);
      }
    };

    if (Platform.OS === 'web') {
      if ((globalThis as any).confirm?.('Update Bundle Config\n\nThis will replace the bundle configuration with all items currently in your Content Store. Continue?') !== false) {
        doUpdate();
      }
    } else {
      Alert.alert(
        'Update Bundle Config',
        'This will replace the bundle configuration with all items currently in your Content Store. Continue?',
        [
          { text: 'Cancel', style: 'cancel' },
          { text: 'Update', onPress: doUpdate }
        ]
      );
    }
  };

  const handleViewBundleConfigDetails = () => {
    try {
      const bundleGenerator = new ContentBundleGenerator(storage, useContentStore, contentService);
      const bundleConfigManager = bundleGenerator.getBundleConfigManager();
      const configDetails = bundleConfigManager.getBundleConfigDetails();
      
      const adminSegmentsList = configDetails.adminSegments.length > 0 
        ? configDetails.adminSegments.join('\n• ')
        : 'None';
      
      const otherContentList = configDetails.otherContent.length > 0
        ? configDetails.otherContent.join('\n• ')
        : 'None';

      const title = 'Bundle Configuration Details';
      const message =
        `Total Items: ${configDetails.totalItems}\n` +
        `Version: ${configDetails.version}\n\n` +
        `AdminSegments (${configDetails.adminSegments.length}):\n• ${adminSegmentsList}\n\n` +
        `Other Content (${configDetails.otherContent.length}):\n• ${otherContentList}`;

      // Always update lastOperation so details are visible inline on web
      setLastOperation(`📦 Bundle Config — ${message.replace(/\n/g, ' | ')}`);

      if (Platform.OS === 'web') {
        (globalThis as any).alert?.(`${title}\n\n${message}`);
      } else {
        Alert.alert(title, message, [{ text: 'OK' }]);
      }
      
    } catch (error) {
      const msg = `Failed to get bundle config details: ${error instanceof Error ? error.message : String(error)}`;
      setLastOperation(`❌ ${msg}`);
      if (Platform.OS === 'web') {
        (globalThis as any).alert?.(msg);
      } else {
        Alert.alert('Error', msg);
      }
    }
  };

  const handleResetBundleConfig = async () => {
    if (!storage) {
      Alert.alert('Error', 'Storage not available');
      return;
    }

    const doReset = async () => {
      setIsLoading(true);
      setLastOperation('📋🔄 Resetting bundle config to defaults...');

      try {
        const bundleGenerator = new ContentBundleGenerator(storage, useContentStore, contentService);
        const bundleConfigManager = bundleGenerator.getBundleConfigManager();

        await bundleConfigManager.resetBundleConfig();

        setLastOperation('✅ Bundle config reset to defaults');
        if (Platform.OS === 'web') {
          (globalThis as any).alert?.('Bundle Config Reset\n\nBundle configuration has been reset to default items.');
        } else {
          Alert.alert('Success', 'Bundle configuration has been reset to default items.');
        }
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : String(error);
        setLastOperation(`❌ Failed to reset bundle config: ${errorMessage}`);
        Alert.alert('Error', `Failed to reset bundle config: ${errorMessage}`);
      } finally {
        setIsLoading(false);
      }
    };

    if (Platform.OS === 'web') {
      if ((globalThis as any).confirm?.('Reset Bundle Config\n\nThis will reset the bundle configuration to the default items (PHQ-9, Standard Milestones, Premium Milestones). Continue?') !== false) {
        doReset();
      }
    } else {
      Alert.alert(
        'Reset Bundle Config',
        'This will reset the bundle configuration to the default items (PHQ-9, Standard Milestones, Premium Milestones). Continue?',
        [
          { text: 'Cancel', style: 'cancel' },
          { text: 'Reset', style: 'destructive', onPress: doReset }
        ]
      );
    }
  };

  const handleClearAllContent = () => {
    const doClear = async () => {
      console.log('🗑️ DebugView: Attempting to clear all content and reset bundle flag...');
      console.log(`   useContentStore available: ${!!useContentStore}`);
      console.log(`   storage available: ${!!storage}`);

      if (useContentStore && storage) {
        try {
          setIsLoading(true);
          setLastOperation('🗑️ Clearing all content and resetting bundle flag...');

          const state = useContentStore.getState();
          console.log(`   ContentStore state: ${!!state}`);
          console.log(`   Available methods: ${state ? Object.keys(state).filter(k => typeof state[k] === 'function') : 'N/A'}`);

          const clearAll = state?.clearAllContent;
          console.log(`   clearAllContent function: ${!!clearAll}`);

          if (clearAll) {
            // Step 1: Clear all content from ContentStore
            clearAll();
            console.log('   ✅ ContentStore cleared successfully');

            // Step 2: Clear the bundle loading flag by removing bundle-related storage keys
            try {
              await storage.removeItem('bundled_content_initialized');
              await storage.removeItem('bundled_content_version');
              await storage.removeItem('bundled_content_timestamp');
              await storage.removeItem('bundle_initialization_complete');
              console.log('   ✅ Bundle initialization flags cleared');
              await storage.removeItem('cached_bundled_content');
              console.log('   ✅ Cached bundle data cleared');
            } catch (flagError) {
              console.warn('   ⚠️ Some bundle flags may not have existed:', flagError);
            }

            setLastOperation('✅ All content and bundle flags cleared');
            loadContentEntries();
            Alert.alert('Success', 'All content cleared from store and bundle loading flag reset.');
            console.log('   ✅ Content and bundle flags cleared successfully');
          } else {
            setLastOperation('❌ Clear function not available');
            Alert.alert('Error', 'Clear function not available');
            console.log('   ❌ clearAllContent function not found');
          }
        } catch (error) {
          console.error('   ❌ Error clearing content:', error);
          setLastOperation(`❌ Error clearing content: ${error instanceof Error ? error.message : String(error)}`);
          Alert.alert('Error', `Failed to clear content: ${error instanceof Error ? error.message : String(error)}`);
        } finally {
          setIsLoading(false);
        }
      } else {
        setLastOperation('❌ Content store or storage not available');
        Alert.alert('Error', 'Content store or storage not available');
        console.log('   ❌ Required services not available');
      }
    };

    if (Platform.OS === 'web') {
      if ((globalThis as any).confirm?.('Clear All Content\n\nThis will remove all downloaded content from the local store and reset the bundle loading flag.') !== false) {
        doClear();
      }
    } else {
      Alert.alert(
        'Clear All Content',
        'This will remove all downloaded content from the local store and reset the bundle loading flag. Continue?',
        [
          { text: 'Cancel', style: 'cancel' },
          { text: 'Clear All', style: 'destructive', onPress: doClear }
        ]
      );
    }
  };

  const handleClearAllAdmins = () => {
    const doClearAllAdmins = async () => {
      console.log('🗑️ DebugView: Attempting to clear all AdminStateDto records...');
      
      if (storage) {
        try {
          // Collect keys from two sources:
          // 1. storage.getAllKeys() — enumerates the underlying store directly
          // 2. admin_state_key_registry — AdminStateStorage's own authoritative list
          const allStorageKeys: string[] = await storage.getAllKeys();
          console.log(`   Found ${allStorageKeys.length} total storage keys via getAllKeys()`);

          const registryRaw = await storage.getItem('admin_state_key_registry');
          const registeredStateKeys: string[] = registryRaw ? JSON.parse(registryRaw) : [];
          console.log(`   Found ${registeredStateKeys.length} admin_state keys in registry`);

          // Union both sets then filter to AdminStateDto keys ONLY
          // Preserve: admin_assessment_list, admin_request_list, admin_responses_*
          const combinedKeys = Array.from(new Set([...allStorageKeys, ...registeredStateKeys]));
          const adminStateKeys = combinedKeys.filter((key: string) =>
            key.startsWith('admin_state_') ||
            key === 'admin_state_key_registry'
          );
          console.log(`   Found ${adminStateKeys.length} AdminStateDto keys to clear:`, adminStateKeys);
          
          if (adminStateKeys.length > 0) {
            for (const key of adminStateKeys) {
              await storage.removeItem(key);
              console.log(`   🗑️ Removed: ${key}`);
            }
            
            setLastOperation(`✅ Cleared ${adminStateKeys.length} AdminStateDto entries`);
            if (Platform.OS === 'web') {
              (globalThis as any).alert?.(`Success\n\nCleared ${adminStateKeys.length} AdminStateDto entries from storage`);
            } else {
              Alert.alert('Success', `Cleared ${adminStateKeys.length} AdminStateDto entries from storage`);
            }
            console.log('   ✅ All AdminStateDto records cleared successfully');
          } else {
            setLastOperation('ℹ️ No AdminStateDto entries found');
            if (Platform.OS === 'web') {
              (globalThis as any).alert?.('Info\n\nNo AdminStateDto entries found in storage');
            } else {
              Alert.alert('Info', 'No AdminStateDto entries found in storage');
            }
            console.log('   ℹ️ No AdminStateDto entries found to clear');
          }
        } catch (error) {
          console.error('   ❌ Error clearing AdminStateDto records:', error);
          setLastOperation(`❌ Error clearing AdminStateDto records: ${error instanceof Error ? error.message : String(error)}`);
          if (Platform.OS === 'web') {
            (globalThis as any).alert?.(`Error\n\nFailed to clear AdminStateDto records: ${error instanceof Error ? error.message : String(error)}`);
          } else {
            Alert.alert('Error', `Failed to clear AdminStateDto records: ${error instanceof Error ? error.message : String(error)}`);
          }
        }
      } else {
        setLastOperation('❌ Storage not available');
        if (Platform.OS === 'web') {
          (globalThis as any).alert?.('Error\n\nStorage not available');
        } else {
          Alert.alert('Error', 'Storage not available');
        }
        console.log('   ❌ Storage not available');
      }
    };

    if (Platform.OS === 'web') {
      if ((globalThis as any).confirm?.('Clear All Admin States\n\nThis will remove all AdminStateDto entries (in-progress and completed assessment state) from storage. Assessment definitions and completed response records will be preserved. Continue?') !== false) {
        doClearAllAdmins();
      }
    } else {
      Alert.alert(
        'Clear All Admin States',
        'This will remove all AdminStateDto entries (in-progress and completed assessment state) from storage. Assessment definitions and completed response records will be preserved. Continue?',
        [
          { text: 'Cancel', style: 'cancel' },
          { text: 'Clear All', style: 'destructive', onPress: doClearAllAdmins }
        ]
      );
    }
  };

  const handleClearAllResponses = () => {
    const doClearAllResponses = async () => {
      console.log('🗑️ DebugView: Attempting to clear all AdminResponseDto records...');

      if (storage) {
        try {
          // Collect keys from two sources:
          // 1. storage.getAllKeys() — enumerates the underlying store directly
          // 2. admin_response_key_registry — AdminResponseStorage's own authoritative list
          const allStorageKeys: string[] = await storage.getAllKeys();
          console.log(`   Found ${allStorageKeys.length} total storage keys via getAllKeys()`);

          const registryRaw = await storage.getItem('admin_response_key_registry');
          const registeredResponseKeys: string[] = registryRaw ? JSON.parse(registryRaw) : [];
          console.log(`   Found ${registeredResponseKeys.length} admin_response keys in registry`);

          // Union both sets then filter to AdminResponseDto keys ONLY
          const combinedKeys = Array.from(new Set([...allStorageKeys, ...registeredResponseKeys]));
          const responseKeys = combinedKeys.filter((key: string) =>
            key.startsWith('admin_responses_') ||
            key === 'admin_response_key_registry'
          );
          console.log(`   Found ${responseKeys.length} AdminResponseDto keys to clear:`, responseKeys);

          if (responseKeys.length > 0) {
            for (const key of responseKeys) {
              await storage.removeItem(key);
              console.log(`   🗑️ Removed: ${key}`);
            }
            setAssessmentResponses([]);

            setLastOperation(`✅ Cleared ${responseKeys.length} AdminResponseDto entries`);
            if (Platform.OS === 'web') {
              (globalThis as any).alert?.(`Success\n\nCleared ${responseKeys.length} AdminResponseDto entries from storage`);
            } else {
              Alert.alert('Success', `Cleared ${responseKeys.length} AdminResponseDto entries from storage`);
            }
            console.log('   ✅ All AdminResponseDto records cleared successfully');
          } else {
            setLastOperation('ℹ️ No AdminResponseDto entries found');
            if (Platform.OS === 'web') {
              (globalThis as any).alert?.('Info\n\nNo AdminResponseDto entries found in storage');
            } else {
              Alert.alert('Info', 'No AdminResponseDto entries found in storage');
            }
            console.log('   ℹ️ No AdminResponseDto entries found to clear');
          }
        } catch (error) {
          console.error('   ❌ Error clearing AdminResponseDto records:', error);
          setLastOperation(`❌ Error clearing AdminResponseDto records: ${error instanceof Error ? error.message : String(error)}`);
          if (Platform.OS === 'web') {
            (globalThis as any).alert?.(`Error\n\nFailed to clear AdminResponseDto records: ${error instanceof Error ? error.message : String(error)}`);
          } else {
            Alert.alert('Error', `Failed to clear AdminResponseDto records: ${error instanceof Error ? error.message : String(error)}`);
          }
        }
      } else {
        setLastOperation('❌ Storage not available');
        if (Platform.OS === 'web') {
          (globalThis as any).alert?.('Error\n\nStorage not available');
        } else {
          Alert.alert('Error', 'Storage not available');
        }
        console.log('   ❌ Storage not available');
      }
    };

    if (Platform.OS === 'web') {
      if ((globalThis as any).confirm?.('Clear All Responses\n\nThis will remove all local AdminResponseDto records (completed assessment history) for all profiles from storage. In-progress assessment state and assessment definitions will be preserved. Continue?') !== false) {
        doClearAllResponses();
      }
    } else {
      Alert.alert(
        'Clear All Responses',
        'This will remove all local AdminResponseDto records (completed assessment history) for all profiles from storage. In-progress assessment state and assessment definitions will be preserved. Continue?',
        [
          { text: 'Cancel', style: 'cancel' },
          { text: 'Clear All', style: 'destructive', onPress: doClearAllResponses }
        ]
      );
    }
  };

  // Targeted milestone reload functions (preserve existing milestones)
  const handleReloadStandardMilestones = async () => {
    const confirmTitle = 'Reload Standard Milestones';
    const confirmMsg = 'This will update/add Standard milestones from AdminSegment 11255, preserving any existing Premium milestones. Continue?';
    let confirmed: boolean;
    if (Platform.OS === 'web') {
      confirmed = !!(globalThis as any).confirm?.(confirmTitle + '\n\n' + confirmMsg);
    } else {
      confirmed = await new Promise<boolean>(resolve =>
        Alert.alert(confirmTitle, confirmMsg, [
          { text: 'Cancel', style: 'cancel', onPress: () => resolve(false) },
          { text: 'Reload', onPress: () => resolve(true) }
        ])
      );
    }
    if (!confirmed) return;

    console.log('🔄 DebugView: Reloading Standard milestones (preserving Premium)...');

    if (storage && contentService) {
      try {
        setIsLoading(true);
        setLastOperation('🔄 Reloading Standard milestones (preserving Premium)...');

        // Create AdminSegmentStorage instance
        const globalStorage = storage || getRuntimeStorage();
        const adminSegmentStorage = new AdminSegmentStorage(globalStorage);

        // Use targeted Standard milestone reload
        await adminSegmentStorage.reloadStandardMilestones(contentService, '1');

        // Signal MilestonesView to refresh
        (global as any).__milestoneStorageInvalidated = Date.now();
        console.log('🚨 DebugView: Standard milestones reloaded, signaling MilestonesView to refresh');

        setLastOperation('✅ Successfully reloaded Standard milestones (Premium milestones preserved)');
        if (Platform.OS === 'web') {
          (globalThis as any).alert?.('Success\n\nStandard milestones updated/added. Premium milestones preserved. MilestonesView will refresh automatically.');
        } else {
          Alert.alert('Success', 'Standard milestones updated/added. Premium milestones preserved. MilestonesView will refresh automatically.');
        }
      } catch (error) {
        console.error('   ❌ Error reloading Standard milestones:', error);
        setLastOperation(`❌ Error reloading Standard milestones: ${error instanceof Error ? error.message : String(error)}`);
        if (Platform.OS === 'web') {
          (globalThis as any).alert?.(`Error\n\nFailed to reload Standard milestones: ${error instanceof Error ? error.message : String(error)}`);
        } else {
          Alert.alert('Error', `Failed to reload Standard milestones: ${error instanceof Error ? error.message : String(error)}`);
        }
      } finally {
        setIsLoading(false);
      }
    } else {
      setLastOperation('❌ Storage or contentService not available');
      if (Platform.OS === 'web') {
        (globalThis as any).alert?.('Error\n\nStorage or contentService not available');
      } else {
        Alert.alert('Error', 'Storage or contentService not available');
      }
    }
  };

  const handleReloadPremiumMilestones = async () => {
    const confirmTitle = 'Reload Premium Milestones';
    const confirmMsg = 'This will update/add Premium milestones from AdminSegment 11270, preserving any existing Standard milestones. Continue?';
    let confirmed: boolean;
    if (Platform.OS === 'web') {
      confirmed = !!(globalThis as any).confirm?.(confirmTitle + '\n\n' + confirmMsg);
    } else {
      confirmed = await new Promise<boolean>(resolve =>
        Alert.alert(confirmTitle, confirmMsg, [
          { text: 'Cancel', style: 'cancel', onPress: () => resolve(false) },
          { text: 'Reload', onPress: () => resolve(true) }
        ])
      );
    }
    if (!confirmed) return;

    console.log('🔄 DebugView: Reloading Premium milestones (preserving Standard)...');

    if (storage && contentService) {
      try {
        setIsLoading(true);
        setLastOperation('🔄 Reloading Premium milestones (preserving Standard)...');

        // Create AdminSegmentStorage instance
        const globalStorage = storage || getRuntimeStorage();
        const adminSegmentStorage = new AdminSegmentStorage(globalStorage);

        // Use targeted Premium milestone reload
        await adminSegmentStorage.reloadPremiumMilestones(contentService, '1');

        // Signal MilestonesView to refresh
        (global as any).__milestoneStorageInvalidated = Date.now();
        console.log('🚨 DebugView: Premium milestones reloaded, signaling MilestonesView to refresh');

        setLastOperation('✅ Successfully reloaded Premium milestones (Standard milestones preserved)');
        if (Platform.OS === 'web') {
          (globalThis as any).alert?.('Success\n\nPremium milestones updated/added. Standard milestones preserved. MilestonesView will refresh automatically.');
        } else {
          Alert.alert('Success', 'Premium milestones updated/added. Standard milestones preserved. MilestonesView will refresh automatically.');
        }
      } catch (error) {
        console.error('   ❌ Error reloading Premium milestones:', error);
        setLastOperation(`❌ Error reloading Premium milestones: ${error instanceof Error ? error.message : String(error)}`);
        if (Platform.OS === 'web') {
          (globalThis as any).alert?.(`Error\n\nFailed to reload Premium milestones: ${error instanceof Error ? error.message : String(error)}`);
        } else {
          Alert.alert('Error', `Failed to reload Premium milestones: ${error instanceof Error ? error.message : String(error)}`);
        }
      } finally {
        setIsLoading(false);
      }
    } else {
      setLastOperation('❌ Storage or contentService not available');
      if (Platform.OS === 'web') {
        (globalThis as any).alert?.('Error\n\nStorage or contentService not available');
      } else {
        Alert.alert('Error', 'Storage or contentService not available');
      }
    }
  };

  // Milestone management functions
  const handleClearAndReloadMilestones = async (includeStandard: boolean = true, includePremium: boolean = false) => {
    const typeDescription = includeStandard && includePremium ? 'both standard and premium'
      : includeStandard ? 'standard'
      : 'premium';
    const confirmTitle = 'Reload Milestones from AdminSegments';
    const confirmMsg = `This will clear existing milestone storage and reload ${typeDescription} milestones from AdminSegment nodes. Continue?`;
    let confirmed: boolean;
    if (Platform.OS === 'web') {
      confirmed = !!(globalThis as any).confirm?.(confirmTitle + '\n\n' + confirmMsg);
    } else {
      confirmed = await new Promise<boolean>(resolve =>
        Alert.alert(confirmTitle, confirmMsg, [
          { text: 'Cancel', style: 'cancel', onPress: () => resolve(false) },
          { text: 'Reload', style: 'destructive', onPress: () => resolve(true) }
        ])
      );
    }
    if (!confirmed) return;

    console.log('🔄 DebugView: Reloading milestones from AdminSegment nodes...');

    if (storage && contentService) {
      try {
        setIsLoading(true);
        setLastOperation('🔄 Reloading milestones from AdminSegment nodes...');

        // Create AdminSegmentStorage instance using same global storage as MilestonesView
        const globalStorage = storage || getRuntimeStorage();
        const adminSegmentStorage = new AdminSegmentStorage(globalStorage);

        // Reload milestones from AdminSegment nodes
        await adminSegmentStorage.reloadMilestonesFromNodes(
          contentService,
          '1', // Default language
          includeStandard,
          includePremium
        );

        // Signal MilestonesView to refresh its data
        (global as any).__milestoneStorageInvalidated = Date.now();
        console.log('🚨 DebugView: Milestones reloaded, signaling MilestonesView to refresh');

        setLastOperation(`✅ Successfully reloaded ${typeDescription} milestones from AdminSegments`);
        if (Platform.OS === 'web') {
          (globalThis as any).alert?.(`Success\n\nMilestones reloaded from AdminSegment nodes (${typeDescription}). MilestonesView will refresh automatically.`);
        } else {
          Alert.alert('Success', `Milestones reloaded from AdminSegment nodes (${typeDescription}). MilestonesView will refresh automatically.`);
        }
        console.log('   ✅ Milestone reload completed successfully');
      } catch (error) {
        console.error('   ❌ Error reloading milestones:', error);
        setLastOperation(`❌ Error reloading milestones: ${error instanceof Error ? error.message : String(error)}`);
        if (Platform.OS === 'web') {
          (globalThis as any).alert?.(`Error\n\nFailed to reload milestones: ${error instanceof Error ? error.message : String(error)}`);
        } else {
          Alert.alert('Error', `Failed to reload milestones: ${error instanceof Error ? error.message : String(error)}`);
        }
      } finally {
        setIsLoading(false);
      }
    } else {
      setLastOperation('❌ Storage or contentService not available');
      if (Platform.OS === 'web') {
        (globalThis as any).alert?.('Error\n\nStorage or contentService not available');
      } else {
        Alert.alert('Error', 'Storage or contentService not available');
      }
      console.log('   ❌ Required services not available');
    }
  };

  const handleClearMilestones = async () => {
    const confirmTitle = 'Clear Milestone Storage';
    const confirmMsg = 'This will remove all milestone data from storage. Continue?';
    let confirmed: boolean;
    if (Platform.OS === 'web') {
      confirmed = !!(globalThis as any).confirm?.(confirmTitle + '\n\n' + confirmMsg);
    } else {
      confirmed = await new Promise<boolean>(resolve =>
        Alert.alert(confirmTitle, confirmMsg, [
          { text: 'Cancel', style: 'cancel', onPress: () => resolve(false) },
          { text: 'Clear', style: 'destructive', onPress: () => resolve(true) }
        ])
      );
    }
    if (!confirmed) return;

    console.log('🗑️ DebugView: Clearing milestone storage from shared storage...');

    try {
      setIsLoading(true);
      setLastOperation('🗑️ Clearing milestone storage...');

      // Use the same global storage as passed to this component or global storage
      const globalStorage = storage || getRuntimeStorage();

      if (!globalStorage) {
        throw new Error('No storage available');
      }

      // Create milestone storage with the same persistent storage as MilestonesView
      const milestoneStorage = createMasterMilestoneStorage(globalStorage);

      // Clear the master data through the storage interface
      await milestoneStorage.saveMasterData({
        milestones: [],
        milestoneTexts: [],
        milestoneAnswers: [],
        milestoneAnswerTexts: [],
        concernAnswers: [],
        concernAnswerTexts: [],
        dataVersion: Date.now().toString(),
        languages: [],
        lastSyncTimestamp: ''
      });

      // Force MilestonesView to recreate its storage instance on next access
      (global as any).__milestoneStorageInvalidated = Date.now();

      // Signal MilestonesView to refresh its data
      (global as any).__milestoneStorageInvalidated = Date.now();
      console.log('🚨 DebugView: Storage cleared, signaling MilestonesView to refresh');

      // Verify the storage is cleared
      const verifyData = await milestoneStorage.loadMasterData();
      const isCleared = !verifyData || verifyData.milestones.length === 0;

      if (!isCleared) {
        console.warn('   ⚠️ Warning: Storage may not have been fully cleared');
      } else {
        console.log('   ✅ Verified: milestone storage cleared - no milestones remain');
      }

      setLastOperation('✅ Milestone storage cleared');
      if (Platform.OS === 'web') {
        (globalThis as any).alert?.('Success\n\nMilestone storage cleared. MilestonesView will show no data.');
      } else {
        Alert.alert('Success', 'Milestone storage cleared. MilestonesView will show no data.');
      }
      console.log('   ✅ Milestone storage cleared successfully from persistent storage');
    } catch (error) {
      console.error('   ❌ Error clearing milestones:', error);
      setLastOperation(`❌ Error clearing milestones: ${error instanceof Error ? error.message : String(error)}`);
      if (Platform.OS === 'web') {
        (globalThis as any).alert?.(`Error\n\nFailed to clear milestones: ${error instanceof Error ? error.message : String(error)}`);
      } else {
        Alert.alert('Error', `Failed to clear milestones: ${error instanceof Error ? error.message : String(error)}`);
      }
    } finally {
      setIsLoading(false);
    }
  };

  // ─── Concern Management handlers ─────────────────────────────────────────

  const handleExtractConcerns = async () => {
    const globalStorage = storage || getRuntimeStorage();
    if (!globalStorage || !useContentStore) {
      setLastOperation('❌ Storage or content store not available');
      if (Platform.OS === 'web') {
        (globalThis as any).alert?.('Error\n\nStorage or content store not available');
      } else {
        Alert.alert('Error', 'Storage or content store not available');
      }
      return;
    }
    try {
      setIsLoading(true);
      setLastOperation('🔍 Extracting concerns from AdminSegment 11272...');
      const adminSegmentStorage = new AdminSegmentStorage(globalStorage);

      // Fetch the AdminSegment 11272 ContentRecord from the content store
      if (!useContentStore) {
        setLastOperation('❌ Content store not available');
        if (Platform.OS === 'web') {
          (globalThis as any).alert?.('Error\n\nContent store not available.');
        } else {
          Alert.alert('Error', 'Content store not available.');
        }
        return;
      }
      const record = useContentStore.getState().getContent({ contentType: 'AdminSegment', contentTypeId: '11272', languageTypeId: '1' });

      if (!record) {
        setLastOperation('❌ AdminSegment 11272 not found in content store — download it first via Custom Download');
        if (Platform.OS === 'web') {
          (globalThis as any).alert?.('Error\n\nAdminSegment 11272 not in content store.\nUse Custom Download to fetch it first (ID: 11272, type: AdminSegment).');
        } else {
          Alert.alert('Error', 'AdminSegment 11272 not in content store.\nUse Custom Download to fetch it first (ID: 11272, type: AdminSegment).');
        }
        return;
      }

      const concernData = await adminSegmentStorage.extractConcernsFromSegment(record, '1');
      if (!concernData) {
        throw new Error('extractConcernsFromSegment returned null');
      }

      const summary = concernData.groups.map(g => `${g.domainKey}: ${g.items.length} items`).join(', ');
      setLastOperation(`✅ Extracted ${concernData.groups.length} concern groups: ${summary}`);
      if (Platform.OS === 'web') {
        (globalThis as any).alert?.(`Success\n\nExtracted ${concernData.groups.length} concern groups from "${concernData.segmentTitle}"\n\n${summary}`);
      } else {
        Alert.alert('Success', `Extracted ${concernData.groups.length} concern groups from "${concernData.segmentTitle}"\n\n${summary}`);
      }
    } catch (error) {
      console.error('❌ handleExtractConcerns error:', error);
      setLastOperation(`❌ Extract concerns failed: ${error instanceof Error ? error.message : String(error)}`);
      if (Platform.OS === 'web') {
        (globalThis as any).alert?.(`Error\n\nFailed to extract concerns: ${error instanceof Error ? error.message : String(error)}`);
      } else {
        Alert.alert('Error', `Failed to extract concerns: ${error instanceof Error ? error.message : String(error)}`);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleListConcernGroups = async () => {
    const globalStorage = storage || getRuntimeStorage();
    if (!globalStorage) {
      setLastOperation('❌ Storage not available');
      return;
    }
    try {
      setIsLoading(true);
      const raw = await globalStorage.getItem(CONCERN_STORAGE_KEY);
      if (!raw) {
        setLastOperation('ℹ️ No concern data in storage');
        if (Platform.OS === 'web') {
          (globalThis as any).alert?.('No concern data found.\nRun "Extract Concerns" first.');
        } else {
          Alert.alert('No Data', 'No concern data found.\nRun "Extract Concerns" first.');
        }
        return;
      }
      const data: StoredConcernData = typeof raw === 'string' ? JSON.parse(raw) : raw;
      const lines = data.groups.map(g =>
        `${g.domainKey.padEnd(10)} | ${g.mvarName.padEnd(20)} | ${g.items.length} items`
      );
      const msg = `Segment: ${data.segmentTitle}\nExtracted: ${data.extractedAt}\n\n${lines.join('\n')}`;
      setLastOperation(`✅ Listed ${data.groups.length} concern groups`);
      if (Platform.OS === 'web') {
        (globalThis as any).alert?.(msg);
      } else {
        Alert.alert('Concern Groups', msg);
      }
    } catch (error) {
      setLastOperation(`❌ List concerns failed: ${error instanceof Error ? error.message : String(error)}`);
    } finally {
      setIsLoading(false);
    }
  };

  // ── Report Script Tools ─────────────────────────────────────────────────────

  /**
   * Walk a downloaded AdminSegment and produce a hardcoded JS object literal:
   *   const <varName> = { "mvar_key": "label text", ... };
   *
   * Regular questions  → mvarName (lowercase) : questionText
   * Multi-select (MvarTypeId=2) → mvarName_keyValue : answerText  (one entry per answer option)
   *
   * Paste the output into a report script section so it runs self-contained on
   * any platform without needing storage access at execution time.
   */
  const handleGenerateSegmentLabels = async () => {
    const globalStorage = storage || getRuntimeStorage();
    if (!contentService) {
      setSegLabelOutput('// Error: contentService not available — download the segment first');
      return;
    }
    setIsGeneratingLabels(true);
    setSegLabelOutput('');
    try {
      const segStorage = new AdminSegmentStorage(globalStorage);
      const dto = await segStorage.getAdminSegment(segLabelSegmentId.trim(), '1', contentService);
      if (!dto) {
        setSegLabelOutput(`// Segment ${segLabelSegmentId} not found — make sure it has been downloaded`);
        return;
      }

      const labels: Record<string, string> = {};

      // Collect pages from top-level Pages and from every node's Pages array
      const allPages: any[] = [
        ...(dto.Pages || []),
        ...((dto.Nodes || []).flatMap((n: any) => n.Pages || [])),
      ];

      for (const page of allPages) {
        for (const qList of (page.Questions || [])) {
          const question = qList.Question;
          if (!question) continue;

          const mvarName: string = (question.MvarName ?? question.mvarName ?? '').toLowerCase();
          if (!mvarName) continue;

          const typeId = question.MvarTypeId ?? question.mvarTypeId ?? (question as any).mvarTypeID;

          // Prefer language 1; fall back to first available HtmlQuestion entry
          const htmlQArr: any[] = question.HtmlQuestion ?? question.htmlQuestion ?? [];
          const htmlQ: any =
            htmlQArr.find((hq: any) => hq.languageTypeId === '1' || hq.LanguageTypeId === '1') ??
            htmlQArr[0];

          if (typeId === '2' || typeId === 2) {
            // Multi-select: each answer option becomes mvarName_keyValue → aText
            const answerArr: any[] = htmlQ?.htmlAnswer ?? htmlQ?.HtmlAnswer ?? [];
            for (const answer of answerArr) {
              const kv   = answer.keyValue  ?? answer.KeyValue  ?? '';
              const text = answer.aText     ?? answer.AText     ?? answer.text ?? '';
              if (kv) labels[`${mvarName}_${kv}`] = text;
            }
          } else {
            // Regular question: mvarName → question text
            const questionText: string = htmlQ?.qText ?? htmlQ?.QText ?? mvarName;
            labels[mvarName] = questionText;
          }
        }
      }

      const varName = segLabelVarName.trim() || 'segmentLabels';
      setSegLabelOutput(`const ${varName} = ${JSON.stringify(labels, null, 2)};`);
      setLastOperation(`✅ Generated ${Object.keys(labels).length} label entries for segment ${segLabelSegmentId}`);
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setSegLabelOutput(`// Error: ${msg}`);
      setLastOperation(`❌ Label generation failed: ${msg}`);
    } finally {
      setIsGeneratingLabels(false);
    }
  };

  // ── End Report Script Tools ──────────────────────────────────────────────────

  /**
   * Generate Node Order JSON — emits the AdminNodeDto.Order array for every node
   * in the segment as a const object: { nodeName: [ { Name, Val }, ... ], ... }
   */
  const handleGenerateNodeOrder = async () => {
    const globalStorage = storage || getRuntimeStorage();
    if (!contentService) {
      setSegNodeOrderOutput('// Error: contentService not available — download the segment first');
      return;
    }
    setIsGeneratingNodeOrder(true);
    setSegNodeOrderOutput('');
    try {
      const segStorage = new AdminSegmentStorage(globalStorage);
      const dto = await segStorage.getAdminSegment(segLabelSegmentId.trim(), '1', contentService);
      if (!dto) {
        setSegNodeOrderOutput(`// Segment ${segLabelSegmentId} not found — make sure it has been downloaded`);
        return;
      }

      const nodeOrder: Record<string, { Name: string; Val: string }[]> = {};
      for (const node of (dto.Nodes || [])) {
        const name: string = node.Name ?? `node_${Object.keys(nodeOrder).length}`;
        nodeOrder[name] = (node.Order || []).map((o: any) => ({
          Name: o.Name ?? o.name ?? '',
          Val:  o.Val  ?? o.val  ?? '',
        }));
      }

      const varName = (segLabelVarName.trim() || 'segmentLabels') + 'NodeOrder';
      setSegNodeOrderOutput(`const ${varName} = ${JSON.stringify(nodeOrder, null, 2)};`);
      setLastOperation(`✅ Generated node order for ${Object.keys(nodeOrder).length} node(s) in segment ${segLabelSegmentId}`);
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setSegNodeOrderOutput(`// Error: ${msg}`);
      setLastOperation(`❌ Node order generation failed: ${msg}`);
    } finally {
      setIsGeneratingNodeOrder(false);
    }
  };

  /**
   * Generate Answer Labels JSON — emits a const object keyed on mvarId where
   * each value is an object mapping answer dataValue → answerText.
   *
   * Output shape:
   *   const <varName>Answers = {
   *     "mvarName": { "dataValue1": "answerText1", "dataValue2": "answerText2", ... },
   *     ...
   *   };
   *
   * Multi-select (MvarTypeId=2): key is mvarName_keyValue (compound), same as Labels JSON.
   * Regular questions: key is mvarName (plain).
   */
  const handleGenerateAnswerLabels = async () => {
    const globalStorage = storage || getRuntimeStorage();
    if (!contentService) {
      setSegAnswerLabelsOutput('// Error: contentService not available — download the segment first');
      return;
    }
    setIsGeneratingAnswerLabels(true);
    setSegAnswerLabelsOutput('');
    try {
      const segStorage = new AdminSegmentStorage(globalStorage);
      const dto = await segStorage.getAdminSegment(segLabelSegmentId.trim(), '1', contentService);
      if (!dto) {
        setSegAnswerLabelsOutput(`// Segment ${segLabelSegmentId} not found — make sure it has been downloaded`);
        return;
      }

      const answerLabels: Record<string, Record<string, string>> = {};

      const allPages: any[] = [
        ...(dto.Pages || []),
        ...((dto.Nodes || []).flatMap((n: any) => n.Pages || [])),
      ];

      for (const page of allPages) {
        for (const qList of (page.Questions || [])) {
          const question = qList.Question;
          if (!question) continue;

          const mvarName: string = (question.MvarName ?? question.mvarName ?? '').toLowerCase();
          if (!mvarName) continue;

          const typeId = question.MvarTypeId ?? question.mvarTypeId ?? (question as any).mvarTypeID;

          const htmlQArr: any[] = question.HtmlQuestion ?? question.htmlQuestion ?? [];
          const htmlQ: any =
            htmlQArr.find((hq: any) => hq.languageTypeId === '1' || hq.LanguageTypeId === '1') ??
            htmlQArr[0];

          const answerArr: any[] = htmlQ?.htmlAnswer ?? htmlQ?.HtmlAnswer ?? [];
          if (answerArr.length === 0) continue;

          if (typeId === '2' || typeId === 2) {
            // Multi-select: compound key per answer option
            for (const answer of answerArr) {
              const kv       = answer.keyValue  ?? answer.KeyValue  ?? '';
              const dataVal  = answer.dataValue ?? answer.DataValue ?? kv;
              const text     = answer.aText     ?? answer.AText     ?? answer.text ?? '';
              if (!kv) continue;
              const compoundKey = `${mvarName}_${kv}`;
              if (!answerLabels[compoundKey]) answerLabels[compoundKey] = {};
              answerLabels[compoundKey][dataVal] = text;
            }
          } else {
            // Regular question: plain mvarName key
            if (!answerLabels[mvarName]) answerLabels[mvarName] = {};
            for (const answer of answerArr) {
              const dataVal = answer.dataValue ?? answer.DataValue ?? answer.keyValue ?? answer.KeyValue ?? '';
              const text    = answer.aText     ?? answer.AText     ?? answer.text ?? '';
              if (dataVal) answerLabels[mvarName][dataVal] = text;
            }
          }
        }
      }

      const varName = (segLabelVarName.trim() || 'segmentLabels') + 'Answers';
      setSegAnswerLabelsOutput(`const ${varName} = ${JSON.stringify(answerLabels, null, 2)};`);
      setLastOperation(`✅ Generated answer labels for ${Object.keys(answerLabels).length} mvar(s) in segment ${segLabelSegmentId}`);
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setSegAnswerLabelsOutput(`// Error: ${msg}`);
      setLastOperation(`❌ Answer labels generation failed: ${msg}`);
    } finally {
      setIsGeneratingAnswerLabels(false);
    }
  };

  const handleClearConcernStorage = async () => {
    const globalStorage = storage || getRuntimeStorage();
    if (!globalStorage) { setLastOperation('❌ Storage not available'); return; }
    let confirmed: boolean;
    if (Platform.OS === 'web') {
      confirmed = !!(globalThis as any).confirm?.('Clear all stored concern data?');
    } else {
      confirmed = await new Promise<boolean>(resolve =>
        Alert.alert('Clear Concern Storage', 'Remove all stored concern data?', [
          { text: 'Cancel', style: 'cancel', onPress: () => resolve(false) },
          { text: 'Clear', style: 'destructive', onPress: () => resolve(true) },
        ])
      );
    }
    if (!confirmed) return;
    try {
      setIsLoading(true);
      await globalStorage.removeItem(CONCERN_STORAGE_KEY);
      setLastOperation('✅ Concern storage cleared');
      if (Platform.OS === 'web') {
        (globalThis as any).alert?.('Concern storage cleared.');
      } else {
        Alert.alert('Done', 'Concern storage cleared.');
      }
    } catch (error) {
      setLastOperation(`❌ Clear failed: ${error instanceof Error ? error.message : String(error)}`);
    } finally {
      setIsLoading(false);
    }
  };

  // ─── End Concern Management handlers ────────────────────────────────────

  // ─── Action Management handlers ──────────────────────────────────────────

  const handleListActionDefs = async () => {
    const globalStorage = storage || getRuntimeStorage();
    if (!globalStorage) {
      setLastOperation('❌ Storage not available');
      return;
    }
    try {
      setIsLoading(true);
      const actionDefStorage = new ActionDefStorage(globalStorage);
      const defs = await actionDefStorage.getAllActionDefs();
      if (defs.length === 0) {
        setLastOperation('ℹ️ No ActionDef records in storage');
        if (Platform.OS === 'web') {
          (globalThis as any).alert?.('No ActionDef records found.\nDownload an ActionDef from the backend first.');
        } else {
          Alert.alert('No Data', 'No ActionDef records found.\nDownload an ActionDef from the backend first.');
        }
        return;
      }
      const lines = defs.map(d =>
        `Lang: ${d.languageTypeId ?? 'unknown'} | Groups: ${d.actionGroups?.length ?? 0} | Types: ${d.actionTypes?.length ?? 0} | Downloaded: ${d.downloadedAt ?? 'n/a'}`
      );
      const msg = `${defs.length} ActionDef record(s) in storage:\n\n${lines.join('\n')}`;
      setLastOperation(`✅ Listed ${defs.length} ActionDef record(s)`);
      if (Platform.OS === 'web') {
        (globalThis as any).alert?.(msg);
      } else {
        Alert.alert('Action Defs', msg);
      }
    } catch (error) {
      setLastOperation(`❌ List ActionDefs failed: ${error instanceof Error ? error.message : String(error)}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClearActionDefs = async () => {
    const globalStorage = storage || getRuntimeStorage();
    if (!globalStorage) { setLastOperation('❌ Storage not available'); return; }
    let confirmed: boolean;
    if (Platform.OS === 'web') {
      confirmed = !!(globalThis as any).confirm?.('Clear all stored ActionDef data?');
    } else {
      confirmed = await new Promise<boolean>(resolve =>
        Alert.alert('Clear ActionDef Storage', 'Remove all stored ActionDef records?', [
          { text: 'Cancel', style: 'cancel', onPress: () => resolve(false) },
          { text: 'Clear', style: 'destructive', onPress: () => resolve(true) },
        ])
      );
    }
    if (!confirmed) return;
    try {
      setIsLoading(true);
      const actionDefStorage = new ActionDefStorage(globalStorage);
      await actionDefStorage.clearAll();
      setLastOperation('✅ ActionDef storage cleared');
      if (Platform.OS === 'web') {
        (globalThis as any).alert?.('ActionDef storage cleared.');
      } else {
        Alert.alert('Done', 'ActionDef storage cleared.');
      }
    } catch (error) {
      setLastOperation(`❌ Clear ActionDefs failed: ${error instanceof Error ? error.message : String(error)}`);
    } finally {
      setIsLoading(false);
    }
  };

  // ─── End Action Management handlers ──────────────────────────────────────

  // ─── WellCheck Management handlers ───────────────────────────────────────

  const handleListWellCheckSchedules = async () => {
    const globalStorage = storage || getRuntimeStorage();
    if (!globalStorage) {
      setLastOperation('❌ Storage not available');
      return;
    }
    try {
      setIsLoading(true);
      const wellCheckStorage = new WellCheckStorage(globalStorage);
      const lists = await wellCheckStorage.getAllWellCheckDefs();
      if (lists.length === 0) {
        setLastOperation('ℹ️ No WellCheckDef records in storage');
        if (Platform.OS === 'web') {
          (globalThis as any).alert?.('No WellCheckDef records found.\nDownload a WellCheckDef from the backend first.');
        } else {
          Alert.alert('No Data', 'No WellCheckDef records found.\nDownload a WellCheckDef from the backend first.');
        }
        return;
      }
      const lines = lists.map(d =>
        `Lang: ${d.languageTypeId ?? 'unknown'} | Schedules: ${d.schedules?.length ?? 0} | Downloaded: ${d.downloadedAt ?? 'n/a'}`
      );
      const msg = `${lists.length} WellCheckDef record(s) in storage:\n\n${lines.join('\n')}`;
      setLastOperation(`✅ Listed ${lists.length} WellCheckDef record(s)`);
      if (Platform.OS === 'web') {
        (globalThis as any).alert?.(msg);
      } else {
        Alert.alert('WellCheck Schedules', msg);
      }
    } catch (error) {
      setLastOperation(`❌ List WellCheckDef failed: ${error instanceof Error ? error.message : String(error)}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClearWellCheckSchedules = async () => {
    const globalStorage = storage || getRuntimeStorage();
    if (!globalStorage) { setLastOperation('❌ Storage not available'); return; }
    let confirmed: boolean;
    if (Platform.OS === 'web') {
      confirmed = !!(globalThis as any).confirm?.('Clear all stored WellCheckDef data?');
    } else {
      confirmed = await new Promise<boolean>(resolve =>
        Alert.alert('Clear WellCheckDef Storage', 'Remove all stored WellCheck definition records?', [
          { text: 'Cancel', style: 'cancel', onPress: () => resolve(false) },
          { text: 'Clear', style: 'destructive', onPress: () => resolve(true) },
        ])
      );
    }
    if (!confirmed) return;
    try {
      setIsLoading(true);
      const wellCheckStorage = new WellCheckStorage(globalStorage);
      await wellCheckStorage.clearAll();
      setLastOperation('✅ WellCheckDef storage cleared');
      if (Platform.OS === 'web') {
        (globalThis as any).alert?.('WellCheckDef storage cleared.');
      } else {
        Alert.alert('Done', 'WellCheckDef storage cleared.');
      }
    } catch (error) {
      setLastOperation(`❌ Clear WellCheckDef failed: ${error instanceof Error ? error.message : String(error)}`);
    } finally {
      setIsLoading(false);
    }
  };

  // ─── End WellCheck Management handlers ───────────────────────────────────

  // ─── Activities Management handlers ──────────────────────────────────────

  const handleListActivitiesDefs = async () => {
    const globalStorage = storage || getRuntimeStorage();
    if (!globalStorage) {
      setLastOperation('❌ Storage not available');
      return;
    }
    try {
      setIsLoading(true);
      const activitiesDefStorage = new ActivitiesDefStorage(globalStorage);
      const defs = await activitiesDefStorage.getAllActivitiesDefs();
      if (defs.length === 0) {
        setLastOperation('ℹ️ No ActivityDef records in storage');
        if (Platform.OS === 'web') {
          (globalThis as any).alert?.('No ActivityDef records found.\nDownload an ActivityDef from the backend first, or load from bundle.');
        } else {
          Alert.alert('No Data', 'No ActivityDef records found.\nDownload an ActivityDef from the backend first, or load from bundle.');
        }
        return;
      }
      const lines = defs.map(d => {
        const totalItems = d.domains.reduce((sum, dom) =>
          sum + dom.groups.reduce((gs, g) => gs + g.items.length, 0), 0);
        return `SourceId: ${d.sourceId} | Name: ${d.name} | Domains: ${d.domains?.length ?? 0} | Items: ${totalItems} | Lang: ${d.languageTypeId} | Downloaded: ${d.downloadedAt ?? 'n/a'}`;
      });
      const msg = `${defs.length} ActivityDef record(s) in storage:\n\n${lines.join('\n')}`;
      setLastOperation(`✅ Listed ${defs.length} ActivityDef record(s)`);
      if (Platform.OS === 'web') {
        (globalThis as any).alert?.(msg);
      } else {
        Alert.alert('Activities Defs', msg);
      }
    } catch (error) {
      setLastOperation(`❌ List ActivitiesDefs failed: ${error instanceof Error ? error.message : String(error)}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClearActivitiesDefs = async () => {
    const globalStorage = storage || getRuntimeStorage();
    if (!globalStorage) { setLastOperation('❌ Storage not available'); return; }
    let confirmed: boolean;
    if (Platform.OS === 'web') {
      confirmed = !!(globalThis as any).confirm?.('Clear all stored ActivityDef data?');
    } else {
      confirmed = await new Promise<boolean>(resolve =>
        Alert.alert('Clear ActivityDef Storage', 'Remove all stored ActivityDef records?', [
          { text: 'Cancel', style: 'cancel', onPress: () => resolve(false) },
          { text: 'Clear', style: 'destructive', onPress: () => resolve(true) },
        ])
      );
    }
    if (!confirmed) return;
    try {
      setIsLoading(true);
      const activitiesDefStorage = new ActivitiesDefStorage(globalStorage);
      await activitiesDefStorage.clearAll();
      setLastOperation('✅ ActivityDef storage cleared');
      if (Platform.OS === 'web') {
        (globalThis as any).alert?.('ActivityDef storage cleared.');
      } else {
        Alert.alert('Done', 'ActivityDef storage cleared.');
      }
    } catch (error) {
      setLastOperation(`❌ Clear ActivitiesDefs failed: ${error instanceof Error ? error.message : String(error)}`);
    } finally {
      setIsLoading(false);
    }
  };

  // ─── End Activities Management handlers ──────────────────────────────────

  // ─── Guidance Management handlers ────────────────────────────────────────

  const handleListGuidanceDefs = async () => {
    const globalStorage = storage || getRuntimeStorage();
    if (!globalStorage) {
      setLastOperation('❌ Storage not available');
      return;
    }
    try {
      setIsLoading(true);
      const guidanceDefStorage = new GuidanceDefStorage(globalStorage);
      const defs = await guidanceDefStorage.getAllGuidanceDefs();
      if (defs.length === 0) {
        setLastOperation('ℹ️ No GuidanceDef records in storage');
        if (Platform.OS === 'web') {
          (globalThis as any).alert?.('No GuidanceDef records found.\nDownload a GuidanceDef from the backend first, or load from bundle.');
        } else {
          Alert.alert('No Data', 'No GuidanceDef records found.\nDownload a GuidanceDef from the backend first, or load from bundle.');
        }
        return;
      }
      const lines = defs.map(d => {
        const totalItems = d.ageGroups.reduce((sum, g) =>
          sum + g.domains.reduce((ds, dm) => ds + dm.items.length, 0), 0);
        return `SourceId: ${d.sourceId} | Name: ${d.name} | AgeGroups: ${d.ageGroups?.length ?? 0} | Items: ${totalItems} | Lang: ${d.languageTypeId} | Downloaded: ${d.downloadedAt ?? 'n/a'}`;
      });
      const msg = `${defs.length} GuidanceDef record(s) in storage:\n\n${lines.join('\n')}`;
      setLastOperation(`✅ Listed ${defs.length} GuidanceDef record(s)`);
      if (Platform.OS === 'web') {
        (globalThis as any).alert?.(msg);
      } else {
        Alert.alert('Guidance Defs', msg);
      }
    } catch (error) {
      setLastOperation(`❌ List GuidanceDefs failed: ${error instanceof Error ? error.message : String(error)}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClearGuidanceDefs = async () => {
    const globalStorage = storage || getRuntimeStorage();
    if (!globalStorage) { setLastOperation('❌ Storage not available'); return; }
    let confirmed: boolean;
    if (Platform.OS === 'web') {
      confirmed = !!(globalThis as any).confirm?.('Clear all stored GuidanceDef data?');
    } else {
      confirmed = await new Promise<boolean>(resolve =>
        Alert.alert('Clear GuidanceDef Storage', 'Remove all stored GuidanceDef records?', [
          { text: 'Cancel', style: 'cancel', onPress: () => resolve(false) },
          { text: 'Clear', style: 'destructive', onPress: () => resolve(true) },
        ])
      );
    }
    if (!confirmed) return;
    try {
      setIsLoading(true);
      const guidanceDefStorage = new GuidanceDefStorage(globalStorage);
      await guidanceDefStorage.clearAll();
      setLastOperation('✅ GuidanceDef storage cleared');
      if (Platform.OS === 'web') {
        (globalThis as any).alert?.('GuidanceDef storage cleared.');
      } else {
        Alert.alert('Done', 'GuidanceDef storage cleared.');
      }
    } catch (error) {
      setLastOperation(`❌ Clear GuidanceDefs failed: ${error instanceof Error ? error.message : String(error)}`);
    } finally {
      setIsLoading(false);
    }
  };

  // ─── End Guidance Management handlers ────────────────────────────────────

  // ─── Referral Qualification debug handler ───────────────────────────────

  const handleDebugReferComplete = async () => {
    if (!storage) {
      setDebugReferResult('Storage not available.');
      return;
    }
    // Load accountGetInfo from storage so we always have the latest ReferredBy
    const userState = (typeof userStore !== 'boolean' && userStore.getState) ? userStore.getState() : null;
    const accountId = userState?.session?.user?.familyId || userState?.session?.familyAccount?.familyId || '';
    let referredBy: string | undefined;
    if (accountId && storage) {
      try {
        const accountUserStorage = new AccountUserStorage(storage);
        const stored = await accountUserStorage.getAccountGetInfo(accountId);
        referredBy = stored?.ReferredBy;
      } catch { /* ignore */ }
    }
    if (!referredBy) {
      setDebugReferResult('No ReferredBy value found in stored AccountGetInfo. Download it first.');
      return;
    }
    if (!apiService) {
      setDebugReferResult('apiService not available.');
      return;
    }
    setIsDebugReferring(true);
    setDebugReferResult(null);
    try {
      const payload: AccountReferCompleteDto = { ReferredBy: referredBy };
      const request = {
        RequestType: 'account_refer_complete',
        Payload: JSON.stringify(payload),
      };
      const response = await apiService.processServiceRequestWithRefresh(request, 'sync', {
        userStore,
        storage,
      });
      if (response?.success) {
        let responseDto: AccountReferCompleteResponseDto;
        if (typeof response.payload === 'string') {
          responseDto = JSON.parse(response.payload);
        } else {
          responseDto = response.payload as AccountReferCompleteResponseDto;
        }
        // Latch locally
        if (accountId) {
          const accountUserStorage = new AccountUserStorage(storage);
          const dto = await accountUserStorage.getAccountGetInfo(accountId);
          if (dto) {
            const updated = { ...dto, Qualified: responseDto?.Qualified || '1' };
            await accountUserStorage.saveAccountGetInfo(accountId, updated);
            setAccountGetInfo(updated);
          }
        }
        setDebugReferResult(`Success — Qualified: "${responseDto?.Qualified ?? '(empty)'}"`);
        setLastOperation(`✅ account_refer_complete submitted (ReferredBy: ${referredBy})`);
      } else {
        setDebugReferResult(`Failed — ${JSON.stringify(response)}`);
        setLastOperation(`❌ account_refer_complete failed`);
      }
    } catch (error: any) {
      const msg = error?.message ?? String(error);
      setDebugReferResult(`Error — ${msg}`);
      setLastOperation(`❌ account_refer_complete error: ${msg}`);
    } finally {
      setIsDebugReferring(false);
    }
  };

  // ─── AccountGetInfo Management handlers ──────────────────────────────────

  const handleDownloadAccountGetInfo = async () => {
    if (!apiService || !storage) {
      const msg = 'API service or storage not available';
      setLastOperation(`❌ ${msg}`);
      if (Platform.OS === 'web') {
        (globalThis as any).alert?.(`Error\n\n${msg}`);
      } else {
        Alert.alert('Error', msg);
      }
      return;
    }
    try {
      setIsLoading(true);
      setLastOperation('📥 Downloading provider_account_get (AccountSettings) from backend...');

      const userState = (typeof userStore !== 'boolean' && userStore.getState) ? userStore.getState() : null;
      const accountId = userState?.session?.familyAccount?.familyId || '';
      if (!accountId) {
        const msg = 'No account ID available — sign in first';
        setLastOperation(`❌ ${msg}`);
        if (Platform.OS === 'web') {
          (globalThis as any).alert?.(`Error\n\n${msg}`);
        } else {
          Alert.alert('Error', msg);
        }
        return;
      }

      const request: ServiceRequestDto = {
        RequestType: 'provider_account_get',
        Payload: JSON.stringify({
          AccountId: accountId,
          Types: ['AccountSettings'],
        } as AccountPracInfoRequestDto),
      };

      console.log('📥 AccountPracInfo(AccountSettings): sending request for accountId:', accountId);

      const response = await apiService.processServiceRequestWithRefresh(request, 'sync', {
        userStore,
        storage,
      });

      const responsePayload =
        (response as any)?.payload
        ?? (response as any)?.Payload
        ?? (response as any)?.result
        ?? (response as any)?.Result
        ?? (response as any)?.data
        ?? (response as any)?.Data
        ?? null;

      if (!response?.success || responsePayload === null || responsePayload === undefined || responsePayload === '') {
        throw new Error(response?.message || 'No data received from backend');
      }

      // Parse payload (provider_account_get shape)
      const dto = normalizeAccountPracInfoPayload(responsePayload) as any;

      console.log('📥 AccountPracInfo: received DTO:', {
        AccountId: dto?.AccountId,
        hasAccountSettings: !!dto?.AccountSettings,
        sraUser: dto?.SraUser?.Email || null,
        sraCount: dto?.SraList?.length ?? 0,
        siteCount: dto?.SiteList?.length ?? 0,
        status: dto?.Status,
      });

      // Save to AccountPracInfoStorage for provider bootstrap flows.
      const accountPracInfoStorage = new AccountPracInfoStorage(storage);
      await accountPracInfoStorage.saveAccountPracInfo(accountId, dto as any);
      
      // Dispatch event to refresh display settings based on new AccountSettings
      if (typeof window !== 'undefined' && typeof window.dispatchEvent === 'function') {
        console.log('DebugView: dispatching progroups:sra-config-updated after downloading AccountSettings for accountId:', accountId);
        window.dispatchEvent(new CustomEvent('progroups:sra-config-updated', {
          detail: { accountId },
        }));
      }

      setAccountGetInfo(dto as AccountGetInfoResponseDto);
      setAccountGetInfoSavedAt(new Date().toISOString());

      const summary =
        `AccountId: ${dto.AccountId}\n` +
        `AccountSettings: ${dto?.AccountSettings ? 'yes' : 'no'}\n` +
        `SraUser: ${dto?.SraUser?.Email || '-'}\n` +
        `SraCount: ${dto?.SraList?.length ?? 0}\n` +
        `SiteCount: ${dto?.SiteList?.length ?? 0}\n` +
        `Status: ${dto?.Status ?? '-'}`;

      setLastOperation(`✅ AccountPracInfo downloaded and saved for account ${accountId}`);
      if (Platform.OS === 'web') {
        (globalThis as any).alert?.(`AccountPracInfo Downloaded\n\n${summary}`);
      } else {
        Alert.alert('AccountPracInfo Downloaded', summary);
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      setLastOperation(`❌ AccountPracInfo download failed: ${errorMessage}`);
      if (Platform.OS === 'web') {
        (globalThis as any).alert?.(`Error\n\nAccountPracInfo download failed: ${errorMessage}`);
      } else {
        Alert.alert('Error', `AccountPracInfo download failed: ${errorMessage}`);
      }
      console.error('AccountPracInfo download error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleLoadAccountGetInfo = async () => {
    if (!storage) {
      setLastOperation('❌ Storage not available');
      return;
    }
    try {
      setIsLoading(true);
      const userState = (typeof userStore !== 'boolean' && userStore.getState) ? userStore.getState() : null;
      const accountId = userState?.session?.familyAccount?.familyId || '';
      if (!accountId) {
        const msg = 'No account ID available — sign in first';
        setLastOperation(`❌ ${msg}`);
        if (Platform.OS === 'web') {
          (globalThis as any).alert?.(`Error\n\n${msg}`);
        } else {
          Alert.alert('Error', msg);
        }
        return;
      }

      const accountUserStorage = new AccountUserStorage(storage);
      const dto = await accountUserStorage.getAccountGetInfo(accountId);

      if (!dto) {
        setLastOperation('ℹ️ No AccountGetInfo found in storage — download it first');
        if (Platform.OS === 'web') {
          (globalThis as any).alert?.('No Data\n\nNo AccountGetInfo found in storage.\nUse "Download AccountGetInfo" to fetch it first.');
        } else {
          Alert.alert('No Data', 'No AccountGetInfo found in storage.\nUse "Download AccountGetInfo" to fetch it first.');
        }
        return;
      }

      const savedAt = (dto as any)._savedAt || 'unknown';
      setAccountGetInfo(dto);
      setAccountGetInfoSavedAt(savedAt);

      const userLines = (dto.accountusers || []).map(u =>
        `  • ${u.Name} (${u.UserId}) — ${u.Status}`
      ).join('\n');
      const connLines = (dto.connections || []).map(c =>
        `  • ${c.DName} [${c.PatConnId}] — ${c.Status}`
      ).join('\n');

      const msg =
        `AccountId: ${dto.AccountId}\n` +
        `IsPremium: ${dto.IsPremium} | Status: ${dto.Status}\n` +
        `Saved: ${savedAt}\n\n` +
        `Users (${dto.accountusers?.length ?? 0}):\n${userLines || '  (none)'}\n\n` +
        `Connections (${dto.connections?.length ?? 0}):\n${connLines || '  (none)'}`;

      setLastOperation(`✅ Loaded AccountGetInfo for account ${accountId}`);
      if (Platform.OS === 'web') {
        (globalThis as any).alert?.(`AccountGetInfo\n\n${msg}`);
      } else {
        Alert.alert('AccountGetInfo', msg);
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      setLastOperation(`❌ Load AccountGetInfo failed: ${errorMessage}`);
      console.error('Load AccountGetInfo error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClearAccountGetInfo = async () => {
    if (!storage) { setLastOperation('❌ Storage not available'); return; }
    let confirmed: boolean;
    if (Platform.OS === 'web') {
      confirmed = !!(globalThis as any).confirm?.('Clear stored AccountGetInfo data?');
    } else {
      confirmed = await new Promise<boolean>(resolve =>
        Alert.alert('Clear AccountGetInfo', 'Remove stored AccountGetInfo data for this account?', [
          { text: 'Cancel', style: 'cancel', onPress: () => resolve(false) },
          { text: 'Clear', style: 'destructive', onPress: () => resolve(true) },
        ])
      );
    }
    if (!confirmed) return;
    try {
      setIsLoading(true);
      const userState = (typeof userStore !== 'boolean' && userStore.getState) ? userStore.getState() : null;
      const accountId = userState?.session?.familyAccount?.familyId || '';
      if (!accountId) {
        setLastOperation('❌ No account ID available');
        return;
      }
      const accountUserStorage = new AccountUserStorage(storage);
      await accountUserStorage.clearAccountGetInfo(accountId);
      setAccountGetInfo(null);
      setAccountGetInfoSavedAt(null);
      setLastOperation(`✅ AccountGetInfo cleared for account ${accountId}`);
      if (Platform.OS === 'web') {
        (globalThis as any).alert?.('AccountGetInfo storage cleared.');
      } else {
        Alert.alert('Done', 'AccountGetInfo storage cleared.');
      }
    } catch (error) {
      setLastOperation(`❌ Clear AccountGetInfo failed: ${error instanceof Error ? error.message : String(error)}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleRefreshAccountPracInfo = async () => {
    if (!apiService || !storage) {
      const msg = 'API service or storage not available';
      setLastOperation(`❌ ${msg}`);
      if (Platform.OS === 'web') {
        (globalThis as any).alert?.(`Error\n\n${msg}`);
      } else {
        Alert.alert('Error', msg);
      }
      return;
    }

    try {
      setIsLoading(true);
      setLastOperation('🔄 Refreshing provider_account_get account data...');

      const userState = (typeof userStore !== 'boolean' && userStore.getState) ? userStore.getState() : null;
      const accountId = userState?.session?.user?.familyId || userState?.session?.familyAccount?.familyId || '';
      if (!accountId) {
        const msg = 'No account ID available — sign in first';
        setLastOperation(`❌ ${msg}`);
        if (Platform.OS === 'web') {
          (globalThis as any).alert?.(`Error\n\n${msg}`);
        } else {
          Alert.alert('Error', msg);
        }
        return;
      }

      const service = new AccountPracInfoService(apiService);
      const request: AccountPracInfoRequestDto = {
        AccountId: accountId,
        Types: ACCOUNT_PRAC_INFO_TYPES,
      };

      const dto = await service.getAccountPracInfo(request, {
        userStore,
        storage,
      });

      const pracInfoStorage = new AccountPracInfoStorage(storage);
      await pracInfoStorage.saveAccountPracInfo(accountId, dto);
      
      // Dispatch event to refresh display settings based on new AccountSettings
      if (typeof window !== 'undefined' && typeof window.dispatchEvent === 'function') {
        console.log('DebugView: dispatching progroups:sra-config-updated after refreshing account data for accountId:', accountId);
        window.dispatchEvent(new CustomEvent('progroups:sra-config-updated', {
          detail: { accountId },
        }));
      }

      (globalThis as any).__proGroupsAccountPracInfoHydratedByAccountId = {
        ...((globalThis as any).__proGroupsAccountPracInfoHydratedByAccountId ?? {}),
        [accountId]: true,
      };

      const summary =
        `AccountId: ${dto.AccountId}\n` +
        `Touchpoints: ${dto.Touchpoints?.NotifyMsgs?.length ?? 0}\n` +
        `AdminOptions: ${dto.AdminOptions?.length ?? 0}\n` +
        `SraList: ${dto.SraList?.length ?? 0}`;

      setLastOperation(`✅ provider_account_get refreshed and saved for account ${accountId}`);
      if (Platform.OS === 'web') {
        (globalThis as any).alert?.(`Account Practice Info Refreshed\n\n${summary}`);
      } else {
        Alert.alert('Account Practice Info Refreshed', summary);
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      setLastOperation(`❌ provider_account_get refresh failed: ${errorMessage}`);
      if (Platform.OS === 'web') {
        (globalThis as any).alert?.(`Error\n\nprovider_account_get refresh failed: ${errorMessage}`);
      } else {
        Alert.alert('Error', `provider_account_get refresh failed: ${errorMessage}`);
      }
      console.error('provider_account_get refresh error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // ─── End AccountGetInfo Management handlers ───────────────────────────────

  const handleDownloadContent = async () => {
    if (!apiService) {
      Alert.alert('Error', 'API service not available');
      return;
    }

    setIsLoading(true);
    setLastOperation(`Downloading ${customContentType} (ID: ${customContentId}, Lang: ${customLanguageId})...`);

    try {
      // Ensure we have auth token
      const storedToken = storage ? await storage.getItem('auth_token') : null;
      if (!storedToken) {
        setLastOperation('❌ No auth token - try re-login first');
        Alert.alert('Error', 'No authentication token. Please re-login first.');
        return;
      }

      apiService.setAuthToken(storedToken);

      // Create service request
      const serviceRequest = {
        RequestType: 'CONTENT_GET',
        Payload: JSON.stringify({
          ContentType: customContentType,
          ContentTypeId: customContentId,
          ContentLanguageTypeId: customLanguageId
        })
      };

      console.log('🔄 Debug Download: Requesting content...', serviceRequest);

      const response = await apiService.processServiceRequestWithRefresh(serviceRequest, 'content', {
        userStore,
        storage,
      });
      
      if (response?.success && response?.payload) {
        setLastOperation(`✅ Downloaded ${customContentType} successfully`);
        
        // If we have a content store, process and store the content
        if (useContentStore) {
          try {
            console.log('🔄 Processing and storing content in ContentStore...');
            console.log('   Response payload:', response.payload);
            
            // Parse the response payload to TablesDto first
            const tablesDto = parseServiceResponse(response.payload);
            console.log('   Parsed TablesDto:', tablesDto);
            
            // Extract the first table from the Tables array
            if (!tablesDto.Tables || !Array.isArray(tablesDto.Tables) || tablesDto.Tables.length === 0) {
              throw new Error('No tables found in response or invalid table structure');
            }
            
            const tableData: TableData = tablesDto.Tables[0];
            console.log('   Extracted TableData:', tableData);
            
            // Validate TableData structure
            if (!tableData.Schema || !tableData.Data || !tableData.Data.Rows) {
              throw new Error('Invalid TableData structure - missing Schema, Data, or Rows');
            }
            
            // Create content query for storage
            const query = createContentQuery(
              customContentType,
              customContentId,
              customLanguageId
            );
            
            // Store in ContentStore
            const contentStore = useContentStore.getState();
            contentStore.addOrUpdateContent(query, tableData);
            
            console.log('✅ Content successfully stored in ContentStore');
            setLastOperation(`✅ Downloaded and stored ${customContentType} successfully`);
            
            // Special processing for AdminAssessment content type
            if (customContentType === 'AdminAssessment') {
              try {
                console.log('🔄 Processing AdminAssessment content — unpacking into AdminAssessmentStorage...');

                const globalStorage = getRuntimeStorage() || storage;
                const adminAssessmentStorage = new AdminAssessmentStorage(globalStorage);
                const extracted = await adminAssessmentStorage.repopulateFromContentStore(useContentStore.getState());

                if (extracted > 0) {
                  console.log(`✅ AdminAssessment: extracted ${extracted} assessment(s)`);
                  setLastOperation(`✅ AdminAssessment downloaded and stored (${extracted} assessment(s))`);
                } else {
                  console.warn('⚠️ AdminAssessment: repopulateFromContentStore returned 0 — check content_body format');
                  setLastOperation('⚠️ Downloaded but failed to unpack AdminAssessment');
                }
              } catch (processError) {
                console.error('❌ Error processing AdminAssessment:', processError);
                setLastOperation(`⚠️ Downloaded but failed to process AdminAssessment: ${processError instanceof Error ? processError.message : String(processError)}`);
              }
            }
            
            // Special processing for AdminSegment content type — unpack content_body into AdminSegmentStorage
            if (customContentType === 'AdminSegment') {
              try {
                console.log('🔄 Processing AdminSegment content — unpacking into AdminSegmentStorage...');

                // Retrieve the ContentRecord we just stored so unpackAdminSegment can work with it
                const storedRecord = useContentStore.getState().getContent({
                  contentType: customContentType,
                  contentTypeId: customContentId,
                  languageTypeId: customLanguageId,
                });

                if (!storedRecord) {
                  throw new Error('ContentRecord not found immediately after storing — unexpected');
                }

                const globalStorage = getRuntimeStorage() || storage;
                const adminSegmentStorage = new AdminSegmentStorage(globalStorage);
                const adminSegmentDto = await adminSegmentStorage.unpackAdminSegment(storedRecord);

                if (adminSegmentDto) {
                  console.log(`✅ AdminSegment unpacked and stored: ${adminSegmentDto.Title} (${adminSegmentDto.SurveybId})`);
                  setLastOperation(`✅ AdminSegment "${adminSegmentDto.Title}" downloaded and stored`);
                } else {
                  console.warn('⚠️ unpackAdminSegment returned null — check content_body format');
                  setLastOperation(`⚠️ Downloaded but failed to unpack AdminSegment`);
                }
              } catch (segmentError) {
                console.error('❌ Error processing AdminSegment:', segmentError);
                setLastOperation(`⚠️ Downloaded but failed to unpack AdminSegment: ${segmentError instanceof Error ? segmentError.message : String(segmentError)}`);
              }
            }

            // Special processing for Base64Image content type — unpack into Base64ImageStorage
            if (customContentType === 'Base64Image') {
              try {
                console.log('🔄 Processing Base64Image content — unpacking into Base64ImageStorage...');

                // Retrieve the ContentRecord we just stored
                const storedRecord = useContentStore.getState().getContent({
                  contentType: customContentType,
                  contentTypeId: customContentId,
                  languageTypeId: customLanguageId,
                });

                if (!storedRecord) {
                  throw new Error('ContentRecord not found immediately after storing — unexpected');
                }

                const globalStorage = getRuntimeStorage() || storage;
                const imageStorage = new Base64ImageStorage(globalStorage);
                const imageDto = await imageStorage.unpackFromContentRecord(storedRecord);

                if (imageDto) {
                  console.log(`✅ Base64Image unpacked and stored: ${imageDto.imageId} (${imageDto.dName})`);
                  setLastOperation(`✅ Base64Image "${imageDto.imageId}" downloaded and stored`);
                } else {
                  console.warn('⚠️ unpackFromContentRecord returned null — check content_body format');
                  setLastOperation('⚠️ Downloaded but failed to unpack Base64Image');
                }
              } catch (imageError) {
                console.error('❌ Error processing Base64Image:', imageError);
                setLastOperation(`⚠️ Downloaded but failed to unpack Base64Image: ${imageError instanceof Error ? imageError.message : String(imageError)}`);
              }
            }

            // Special processing for ActionDef content type — unpack into ActionDefStorage
            if (customContentType === 'ActionDef') {
              try {
                console.log('🔄 Processing ActionDef content — unpacking into ActionDefStorage...');

                const storedRecord = useContentStore.getState().getContent({
                  contentType: customContentType,
                  contentTypeId: customContentId,
                  languageTypeId: customLanguageId,
                });

                if (!storedRecord) {
                  throw new Error('ContentRecord not found immediately after storing — unexpected');
                }

                const globalStorage = getRuntimeStorage() || storage;
                const actionDefStorage = new ActionDefStorage(globalStorage);
                const actionDefDto = await actionDefStorage.unpackFromContentRecord(storedRecord);

                if (actionDefDto) {
                  console.log(`✅ ActionDef unpacked and stored: source="${actionDefDto.source}" lang="${actionDefDto.languageTypeId}" (${actionDefDto.actionGroups.length} groups, ${actionDefDto.actionTypes.length} types)`);
                  setLastOperation(`✅ ActionDef (lang ${actionDefDto.languageTypeId}) downloaded and stored`);
                } else {
                  console.warn('⚠️ unpackFromContentRecord returned null — check content_body format');
                  setLastOperation('⚠️ Downloaded but failed to unpack ActionDef');
                }
              } catch (actionDefError) {
                console.error('❌ Error processing ActionDef:', actionDefError);
                setLastOperation(`⚠️ Downloaded but failed to unpack ActionDef: ${actionDefError instanceof Error ? actionDefError.message : String(actionDefError)}`);
              }
            }

            // Special processing for AppLabels content type — unpack into AppLabelsStorage
            if (customContentType === 'AppLabels') {
              try {
                console.log('🔄 Processing AppLabels content — unpacking into AppLabelsStorage...');

                const storedRecord = useContentStore.getState().getContent({
                  contentType: customContentType,
                  contentTypeId: customContentId,
                  languageTypeId: customLanguageId,
                });

                if (!storedRecord) {
                  throw new Error('ContentRecord not found immediately after storing — unexpected');
                }

                const globalStorage = getRuntimeStorage() || storage;
                const appLabelsDto = await new AppLabelsStorage(globalStorage).unpackFromContentRecord(storedRecord);

                if (appLabelsDto) {
                  const labelCount = Object.keys(appLabelsDto.labels).length;
                  console.log(`✅ AppLabels unpacked and stored: lang="${appLabelsDto.languageTypeId}" (${labelCount} labels)`);
                  setLastOperation(`✅ AppLabels (lang ${appLabelsDto.languageTypeId}, ${labelCount} labels) downloaded and stored`);
                } else {
                  console.warn('⚠️ unpackFromContentRecord returned null — check content_body format');
                  setLastOperation('⚠️ Downloaded but failed to unpack AppLabels');
                }
              } catch (appLabelsError) {
                console.error('❌ Error processing AppLabels:', appLabelsError);
                setLastOperation(`⚠️ Downloaded but failed to unpack AppLabels: ${appLabelsError instanceof Error ? appLabelsError.message : String(appLabelsError)}`);
              }
            }

            // Special processing for WellCheckDef content type — unpack into WellCheckStorage
            if (customContentType === 'WellCheckDef') {
              try {
                console.log('🔄 Processing WellCheckDef content — unpacking into WellCheckStorage...');

                const storedRecord = useContentStore.getState().getContent({
                  contentType: customContentType,
                  contentTypeId: customContentId,
                  languageTypeId: customLanguageId,
                });

                if (!storedRecord) {
                  throw new Error('ContentRecord not found immediately after storing — unexpected');
                }

                const globalStorage = getRuntimeStorage() || storage;
                const wellCheckStorage = new WellCheckStorage(globalStorage);
                const wellCheckDto = await wellCheckStorage.unpackFromContentRecord(storedRecord);

                if (wellCheckDto) {
                  console.log(`✅ WellCheckDef unpacked and stored: lang="${wellCheckDto.languageTypeId}" (${wellCheckDto.schedules.length} schedule(s), ${wellCheckDto.vaccines.length} vaccine(s))`);
                  setLastOperation(`✅ WellCheckDef (lang ${wellCheckDto.languageTypeId}, ${wellCheckDto.schedules.length} schedule(s), ${wellCheckDto.vaccines.length} vaccine(s)) downloaded and stored`);
                } else {
                  console.warn('⚠️ unpackFromContentRecord returned null — check content_body format');
                  setLastOperation('⚠️ Downloaded but failed to unpack WellCheckDef');
                }
              } catch (wellCheckError) {
                console.error('❌ Error processing WellCheckDef:', wellCheckError);
                setLastOperation(`⚠️ Downloaded but failed to unpack WellCheckDef: ${wellCheckError instanceof Error ? wellCheckError.message : String(wellCheckError)}`);

              }
            }

            // Special processing for ActivityDef content type — unpack into ActivitiesDefStorage
            if (customContentType === 'ActivityDef') {
              try {
                console.log('🔄 Processing ActivityDef content — unpacking into ActivitiesDefStorage...');

                const storedRecord = useContentStore.getState().getContent({
                  contentType: customContentType,
                  contentTypeId: customContentId,
                  languageTypeId: customLanguageId,
                });

                if (!storedRecord) {
                  throw new Error('ContentRecord not found immediately after storing — unexpected');
                }

                const globalStorage = getRuntimeStorage() || storage;
                const activitiesDefStorage = new ActivitiesDefStorage(globalStorage);
                const activitiesDefDto = await activitiesDefStorage.unpackFromContentRecord(storedRecord);

                if (activitiesDefDto) {
                  const totalItems = activitiesDefDto.domains.reduce((sum, d) =>
                    sum + d.groups.reduce((gs, g) => gs + g.items.length, 0), 0);
                  console.log(`✅ ActivityDef unpacked and stored: sourceId="${activitiesDefDto.sourceId}" (${activitiesDefDto.domains.length} domains, ${totalItems} items)`);
                  setLastOperation(`✅ ActivityDef (sourceId "${activitiesDefDto.sourceId}", ${activitiesDefDto.domains.length} domain(s)) downloaded and stored`);
                } else {
                  console.warn('⚠️ unpackFromContentRecord returned null — check content_body format');
                  setLastOperation('⚠️ Downloaded but failed to unpack ActivityDef');
                }
              } catch (activityDefError) {
                console.error('❌ Error processing ActivityDef:', activityDefError);
                setLastOperation(`⚠️ Downloaded but failed to unpack ActivityDef: ${activityDefError instanceof Error ? activityDefError.message : String(activityDefError)}`);
              }
            }

            // Special processing for GuidanceDef content type — unpack into GuidanceDefStorage
            if (customContentType === 'GuidanceDef') {
              try {
                console.log('🔄 Processing GuidanceDef content — unpacking into GuidanceDefStorage...');

                const storedRecord = useContentStore.getState().getContent({
                  contentType: customContentType,
                  contentTypeId: customContentId,
                  languageTypeId: customLanguageId,
                });

                if (!storedRecord) {
                  throw new Error('ContentRecord not found immediately after storing — unexpected');
                }

                const globalStorage = getRuntimeStorage() || storage;
                const guidanceDefStorage = new GuidanceDefStorage(globalStorage);
                const guidanceDefDto = await guidanceDefStorage.unpackFromContentRecord(storedRecord);

                if (guidanceDefDto) {
                  const totalItems = guidanceDefDto.ageGroups.reduce((sum, g) =>
                    sum + g.domains.reduce((ds, d) => ds + d.items.length, 0), 0);
                  console.log(`✅ GuidanceDef unpacked and stored: sourceId="${guidanceDefDto.sourceId}" (${guidanceDefDto.ageGroups.length} age groups, ${totalItems} items)`);
                  setLastOperation(`✅ GuidanceDef (sourceId "${guidanceDefDto.sourceId}", ${guidanceDefDto.ageGroups.length} age group(s)) downloaded and stored`);
                } else {
                  console.warn('⚠️ unpackFromContentRecord returned null — check content_body format');
                  setLastOperation('⚠️ Downloaded but failed to unpack GuidanceDef');
                }
              } catch (guidanceDefError) {
                console.error('❌ Error processing GuidanceDef:', guidanceDefError);
                setLastOperation(`⚠️ Downloaded but failed to unpack GuidanceDef: ${guidanceDefError instanceof Error ? guidanceDefError.message : String(guidanceDefError)}`);
              }
            }

            // Special processing for skintypedef content type — unpack into SkinTypeDefStorage
            if (customContentType.trim().toLowerCase() === 'skintypedef') {
              try {
                console.log('🔄 Processing skintypedef content — unpacking into SkinTypeDefStorage...');

                const storedRecord = useContentStore.getState().getContent({
                  contentType: customContentType,
                  contentTypeId: customContentId,
                  languageTypeId: customLanguageId,
                });

                if (!storedRecord) {
                  throw new Error('ContentRecord not found immediately after storing — unexpected');
                }

                const globalStorage = getRuntimeStorage() || storage;
                const skinTypeDefStorage = new SkinTypeDefStorage(globalStorage);
                const skinTypeDefDto = await skinTypeDefStorage.unpackFromContentRecord(storedRecord);

                if (skinTypeDefDto) {
                  // Extract counts for detailed logging
                  const channelInfoCount = skinTypeDefDto.channelInfo?.length || 0;
                  const skinPhiCount = skinTypeDefDto.skinPhi?.length || 0;
                  const sraTypesCount = skinTypeDefDto.sraTypes?.length || 0;
                  const templateOverridesCount = skinTypeDefDto.templateOverrides?.length || 0;
                  const assessmentListCount = skinTypeDefDto.assessmentList?.length || 0;
                  
                  console.log(`✅ skintypedef unpacked and stored: skinType="${skinTypeDefDto.skinType}" lang="${skinTypeDefDto.languageTypeId}"`);
                  console.log(`   ChannelInfo: ${channelInfoCount}, SkinPhi: ${skinPhiCount}, SraTypes: ${sraTypesCount}, TemplateOverrides: ${templateOverridesCount}, AssessmentList: ${assessmentListCount}`);
                  
                  // Display detailed counts in a modal dialog
                  const detailsMessage = `SkinType: ${skinTypeDefDto.skinType}\nLanguage: ${skinTypeDefDto.languageTypeId}\n\n` +
                    `ChannelInfo: ${channelInfoCount}\n` +
                    `SkinPhi: ${skinPhiCount}\n` +
                    `SraTypes: ${sraTypesCount}\n` +
                    `TemplateOverrides: ${templateOverridesCount}\n` +
                    `AssessmentList: ${assessmentListCount}`;
                  
                  if (Platform.OS === 'web') {
                    (globalThis as any).alert?.(`SkinTypeDef Downloaded Successfully\n\n${detailsMessage}`);
                  } else {
                    Alert.alert('SkinTypeDef Downloaded', detailsMessage);
                  }
                  
                  setLastOperation(`✅ SkinTypeDef (${skinTypeDefDto.skinType}) downloaded and stored`);
                } else {
                  console.warn('⚠️ unpackFromContentRecord returned null — check content_body format');
                  setLastOperation('⚠️ Downloaded but failed to unpack skintypedef');
                }
              } catch (skinTypeDefError) {
                console.error('❌ Error processing skintypedef:', skinTypeDefError);
                setLastOperation(`⚠️ Downloaded but failed to unpack skintypedef: ${skinTypeDefError instanceof Error ? skinTypeDefError.message : String(skinTypeDefError)}`);
              }
            }

            // Refresh the content list to show the new entry
            loadContentEntries();
            // Refresh the assessment list if AdminAssessment was downloaded
            if (customContentType === 'AdminAssessment') {
              loadAssessmentEntries();
            }
          } catch (storeError) {
            console.error('❌ Error storing content:', storeError);
            setLastOperation(`⚠️ Downloaded but failed to store: ${storeError instanceof Error ? storeError.message : String(storeError)}`);
          }
        }
        
        // Show generic success alert only if not SkinTypeDef (which shows its own detailed alert)
        if (customContentType.trim().toLowerCase() !== 'skintypedef') {
          Alert.alert('Success', `${customContentType} downloaded successfully`);
        }
      } else {
        setLastOperation(`❌ Download failed for ${customContentType}`);
        Alert.alert('Error', `Failed to download ${customContentType}`);
      }
      
    } catch (error) {
      console.error('Download error:', error);
      setLastOperation(`❌ Download error: ${error instanceof Error ? error.message : String(error)}`);
      Alert.alert('Error', `Download failed: ${error instanceof Error ? error.message : String(error)}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteContent = useCallback(async (contentId: string, contentType: string) => {
    const confirmTitle = 'Delete Content';
    const confirmMsg = `Delete ${contentType} (${contentId})?`;
    let confirmed: boolean;
    if (Platform.OS === 'web') {
      confirmed = !!(globalThis as any).confirm?.(confirmTitle + '\n\n' + confirmMsg);
    } else {
      confirmed = await new Promise<boolean>(resolve =>
        Alert.alert(confirmTitle, confirmMsg, [
          { text: 'Cancel', style: 'cancel', onPress: () => resolve(false) },
          { text: 'Delete', style: 'destructive', onPress: () => resolve(true) }
        ])
      );
    }
    if (!confirmed || !useContentStore) {
      return;
    }

    const state = useContentStore.getState();
    // Find the content record to get the proper query parameters
    const record = state.records.find((r: any) => r.id === contentId);
    if (!record) {
      setLastOperation('❌ Content record not found');
      return;
    }

    // Use removeContent with proper ContentQuery structure — persisted automatically via
    // the store's zustand persist middleware, same as every other content-store mutation.
    state.removeContent({
      contentType: record.contentType,
      contentTypeId: record.contentTypeId,
      languageTypeId: record.languageTypeId
    });

    // Base64Image records also live in their own dedicated local-storage entry
    // (b64img_{imageId}, read directly by HomeView/ProGroupsMainMenu/etc.) — clear that too
    // so the actual downloaded image bytes don't linger after the content record is gone.
    if (record.contentType === 'Base64Image' && storage) {
      try {
        await new Base64ImageStorage(storage).deleteImage(record.contentTypeId);
      } catch (error) {
        console.warn('DebugView: failed to delete Base64Image storage entry', error);
      }
    }

    setLastOperation(`✅ Deleted ${contentType} (${contentId})`);
    loadContentEntries();
  }, [useContentStore, loadContentEntries, storage]);

  const renderSection = (title: string, children: React.ReactNode) => (
    <View style={styles.section}>
      <Text style={styles.sectionTitle}>{title}</Text>
      {children}
    </View>
  );

  const renderButton = (
    title: string,
    description: string,
    onPress: () => void,
    icon?: string,
    disabled?: boolean
  ) => (
    <TouchableOpacity 
      style={[styles.button, disabled && styles.buttonDisabled]} 
      onPress={onPress}
      disabled={disabled || isLoading}
    >
      <View style={styles.buttonContent}>
        {icon && <Text style={styles.buttonIcon}>{icon}</Text>}
        <View style={styles.buttonText}>
          <Text style={[styles.buttonTitle, disabled && styles.buttonTitleDisabled]}>{title}</Text>
          <Text style={[styles.buttonDescription, disabled && styles.buttonDescriptionDisabled]}>{description}</Text>
        </View>
      </View>
    </TouchableOpacity>
  );

  const renderInfoItem = (label: string, value: string) => (
    <View style={styles.infoItem}>
      <Text style={styles.infoLabel}>{label}</Text>
      <Text style={styles.infoValue}>{value}</Text>
    </View>
  );

  // Helper function to format content entry display with all requested fields
  const formatContentEntryDetails = useCallback((entry: any) => {
    // Reduce console logging to prevent log spam during re-renders
    // console.log('🔍 DebugView: formatContentEntryDetails called for:', entry.contentType);
    
    // Check if we have extracted contentFields from the contentStore
    if (entry.contentFields) {
      const fields = entry.contentFields;
      // console.log('🔍 Using extracted content fields:', fields);
      
      let titleWithId = `${fields.content_title || 'Unknown Title'} (${fields.content_type_id || entry.contentTypeId || 'N/A'})`;
      
      // For AdminSegment entries, try to extract both SurveybId and SurveyAliasId
      if (entry.contentType === 'AdminSegment' && entry.tableData) {
        try {
          // Extract contentBody from tableData structure (like AdminSegmentStorage does)
          const tableData = entry.tableData;
          if (tableData?.Data?.Rows && tableData.Data.Rows.length > 0) {
            const bodyColumnIndex = tableData.Data.Columns.findIndex((col: string) => 
              col.toLowerCase() === 'content_body'
            );
            
            if (bodyColumnIndex !== -1) {
              const contentBody = tableData.Data.Rows[0][bodyColumnIndex];
              
              if (contentBody) {
                // Decode base64 content body to get AdminSegmentDto
                const decodedContent = atob(contentBody);
                const trimmedContent = decodedContent.trim();
                // Skip parse if content appears truncated (incomplete JSON)
                if (!trimmedContent.endsWith('}') && !trimmedContent.endsWith(']')) {
                  // truncated — keep default titleWithId
                } else {
                  const adminSegmentDto = JSON.parse(trimmedContent);
                  
                  const surveybId = adminSegmentDto.SurveybId || entry.contentTypeId || 'N/A';
                  const surveyAliasId = adminSegmentDto.SurveyAliasId || 'N/A';
                  
                  // Show both IDs if SurveyAliasId is different from SurveybId
                  if (surveyAliasId !== 'N/A' && surveyAliasId !== surveybId) {
                    titleWithId = `${fields.content_title || 'Unknown Title'} (ID: ${surveybId}, Alias: ${surveyAliasId})`;
                  } else {
                    titleWithId = `${fields.content_title || 'Unknown Title'} (ID: ${surveybId})`;
                  }
                }
              }
            }
          }
        } catch (error) {
          // Fallback to original format if parsing fails
          titleWithId = `${fields.content_title || 'Unknown Title'} (${fields.content_type_id || entry.contentTypeId || 'N/A'})`;
          console.log('Failed to parse AdminSegment for SurveyAliasId:', error);
        }
      }
      
      return {
        contentType: fields.content_type || entry.contentType || 'Unknown Type',
        titleWithId,
        languageTypeId: fields.content_languagetype_id || entry.languageTypeId || 'N/A',
        modifiedDate: fields.modified_date || 'N/A'
      };
    }
    
    // Fallback to original format if no extracted fields available
    // console.log('🔍 Using fallback format for:', entry.contentType);
    let titleWithId = `ID: ${entry.contentTypeId || 'N/A'}`;
    
    // For AdminSegment entries without contentFields, try to extract IDs directly
    if (entry.contentType === 'AdminSegment' && entry.tableData) {
      try {
        // Extract contentBody from tableData structure (like AdminSegmentStorage does)
        const tableData = entry.tableData;
        if (tableData?.Data?.Rows && tableData.Data.Rows.length > 0) {
          const bodyColumnIndex = tableData.Data.Columns.findIndex((col: string) => 
            col.toLowerCase() === 'content_body'
          );
          
          if (bodyColumnIndex !== -1) {
            const contentBody = tableData.Data.Rows[0][bodyColumnIndex];
            
            if (contentBody) {
              // Decode base64 content body to get AdminSegmentDto
              const decodedContent = atob(contentBody);
              const trimmedContent = decodedContent.trim();
              // Skip parse if content appears truncated (incomplete JSON)
              if (!trimmedContent.endsWith('}') && !trimmedContent.endsWith(']')) {
                titleWithId = `ID: ${entry.contentTypeId || 'N/A'} (truncated)`;
              } else {
                const adminSegmentDto = JSON.parse(trimmedContent);
                
                const surveybId = adminSegmentDto.SurveybId || entry.contentTypeId || 'N/A';
                const surveyAliasId = adminSegmentDto.SurveyAliasId || 'N/A';
                
                // Show both IDs if SurveyAliasId is different from SurveybId
                if (surveyAliasId !== 'N/A' && surveyAliasId !== surveybId) {
                  titleWithId = `ID: ${surveybId}, Alias: ${surveyAliasId}`;
                } else {
                  titleWithId = `ID: ${surveybId}`;
                }
              }
            }
          }
        }
      } catch (error) {
        // Keep original format if parsing fails
        titleWithId = `ID: ${entry.contentTypeId || 'N/A'}`;
        console.log('Failed to parse AdminSegment for SurveyAliasId (fallback):', error);
      }
    }
    
    return {
      contentType: entry.contentType || 'Unknown Type',
      titleWithId,
      languageTypeId: entry.languageTypeId || 'N/A',
      modifiedDate: 'N/A'
    };
  }, []);

  const renderContentEntry = useCallback((entry: any, index: number) => {
    // Reduced logging to prevent console spam during re-renders
    const details = formatContentEntryDetails(entry);
    
    return (
      <View key={index} style={styles.contentEntry}>
        <View style={styles.contentInfo}>
          <Text style={styles.contentType}>{details.contentType}</Text>
          <Text style={styles.contentId}>{details.titleWithId}</Text>
          <Text style={styles.contentLang}>Lang: {details.languageTypeId}</Text>
          <Text style={styles.contentLang}>Modified: {details.modifiedDate}</Text>
          <Text style={styles.contentDate}>Updated: {entry.lastUpdated || 'N/A'}</Text>
        </View>
        <TouchableOpacity 
          style={styles.deleteButton}
          onPress={() => handleDeleteContent(entry.id, entry.contentType)}
        >
          <Text style={styles.deleteButtonText}>🗑️</Text>
        </TouchableOpacity>
      </View>
    );
  }, [formatContentEntryDetails, handleDeleteContent]);

  return (
    <View style={styles.container}>
      {/* Header */}
      <View style={styles.header}>
        <TouchableOpacity onPress={onBack} style={styles.backButton}>
          <Text style={styles.backButtonText}>← Back</Text>
        </TouchableOpacity>
        <View style={styles.titleContainer}>
          <Text style={styles.title}>Development Tools 🔧</Text>
          <Text style={styles.subtitle}>Development and testing utilities</Text>
        </View>
      </View>

      <ScrollView style={styles.content}>
        {/* Account Tools Section */}
        {renderSection(`🔧 Account Tools - ${getAccountStatus()}`, (
          <>
            {/* ISOLATING: Session Info */}
            {false && renderInfoItem('Session Info', getUserSessionInfo())}
            
            {/* ISOLATING: Profile sync status */}
            {false && profileList.length > 0 && (
              <View style={styles.debugSection}>
                <Text style={styles.debugSectionTitle}>🔄 Profile Sync Status</Text>
                <Text style={styles.debugText}>Server Profiles: {profileList.length}</Text>
                {profileList.map((profile, index) => (
                  <Text key={index} style={styles.debugText}>
                    • {profile.profileId || 'Unknown ID'} ({profile.modified ? new Date(profile.modified).toLocaleString() : 'No date'})
                  </Text>
                ))}
                {profileStoreHook && (
                  <Text style={styles.debugText}>
                    Local Profiles: {profileStoreHook.getState().profiles.length}
                  </Text>
                )}
              </View>
            )}
            
            {/* Keep buttons enabled for now */}
            {(() => {
              const webSignOut = (globalThis as any).__webSignOut;
              const canSignOutToSignIn = typeof onSignOut === 'function'
                || (Platform.OS === 'web' && typeof webSignOut === 'function');

              return renderButton(
                canSignOutToSignIn ? 'Sign Out & Go to Sign In' : 'App Account Sign In',
                canSignOutToSignIn
                  ? 'Sign out of the current session and navigate to the sign-in screen'
                  : 'Sign in using stored app credentials or test account sign-in flow',
                async () => {
                  if (canSignOutToSignIn) {
                    setLastOperation('🔐 Signing out and navigating to sign-in...');
                    if (typeof onSignOut === 'function') {
                      await onSignOut();
                      return;
                    }
                    if (Platform.OS === 'web' && typeof webSignOut === 'function') {
                      await webSignOut();
                      return;
                    }
                  }

                  // Legacy fallback: re-authenticate with stored credentials via the
                  // single shared login path (userStore.login), instead of hand-rolling
                  // apiService.authenticate() + manual storage writes here.
                  setIsLoading(true);
                  setLastOperation('🔐 Signing in with app account credentials...');
                  try {
                    if (!apiService || !storage) {
                      throw new Error('API service or storage not available');
                    }

                    const storedEmail = await storage.getItem('user_email');
                    const storedPassword = await storage.getItem('user_password');
                    const storedSignature = await storage.getItem('user_signature');
                    const storedToken = await storage.getItem('auth_token');

                    if (storedEmail && storedPassword && storedSignature) {
                      if (!userStore || typeof userStore.getState !== 'function') {
                        throw new Error('User store not available');
                      }

                      const loginSucceeded = await userStore.getState().login(
                        storedEmail,
                        storedPassword,
                        storedEmail,
                        storedSignature,
                        API_CONSTANTS.DEFAULT_ACCESS_TYPE,
                        '2'
                      );

                      if (loginSucceeded) {
                        const refreshedUser = userStore.getState().session?.user;
                        setJwtTokenTimestamp(new Date().toISOString());

                        const userEmail = refreshedUser?.email || storedEmail;
                        const userId = refreshedUser?.userId || 'unknown';
                        const credType = 'stored app credentials';
                        setLastOperation(`✅ App account sign in successful (${credType}) - userId: ${userId}`);
                        Alert.alert('Success!', `Signed in successfully using ${credType}.\n\nUser ID: ${userId}\nEmail: ${userEmail}`);
                      } else {
                        setLastOperation('❌ App account sign in failed (stored app credentials)');
                        Alert.alert('Sign In Failed', 'Authentication failed using stored credentials.');
                      }
                    } else if (storedToken && storedEmail) {
                      apiService.setAuthToken(storedToken);
                      await storage.setItem('auth_timestamp', new Date().toISOString());
                      setJwtTokenTimestamp(new Date().toISOString());

                      const globalStores = (globalThis as any).__proGroupsStores ?? (globalThis as any).__childNavStores;
                      const globalUserStore = globalStores?.useUserStore;
                      if (globalUserStore) {
                        const userState = globalUserStore.getState();
                        if (!userState.isSessionValid() && userState.checkStoredAuth) {
                          await userState.checkStoredAuth();
                        }
                      }

                      const credType = 'passkey / stored token';
                      setLastOperation(`✅ Auth token restored (${credType}) - email: ${storedEmail}`);
                      Alert.alert('Success!', `Auth token restored using ${credType}.\n\nEmail: ${storedEmail}`);
                    } else {
                      throw new Error('⚠️ No stored credentials or auth token found. Please sign in via the main sign-in screen first.');
                    }
                  } catch (error: any) {
                    const errMsg: string = error?.message || String(error);
                    const is401 = errMsg.includes('401') || errMsg.toLowerCase().includes('unauthorized');
                    if (is401) {
                      await clearAuthState();
                      setLastOperation('❌ Sign in failed (401 Unauthorized) — credentials cleared, please sign in again');
                      Alert.alert('Session Invalid', 'Your credentials are no longer valid (401 Unauthorized).\n\nYou have been signed out. Please sign in again with updated credentials.');
                    } else {
                      setLastOperation('❌ Sign in failed: ' + errMsg);
                      Alert.alert('Sign In Failed', 'Could not sign in: ' + errMsg);
                    }
                  } finally {
                    setIsLoading(false);
                  }
                },
                canSignOutToSignIn ? '🚪' : '🔐',
                canSignOutToSignIn ? false : (isLoading || !apiService || !storage)
              );
            })()}
            
            {renderButton(
              'Test RefreshAuth',
              'Test authentication refresh with stored tokens and API call to /api/authentication/RefreshToken',
              handleTestRefreshAuth,
              '🔄',
              isLoading
            )}
          </>
        ))}
        {/* Debug Settings Section */}
        {renderSection('Debug Settings', (
          <>
            {renderButton(
              'Enable Debug Info',
              `Current: ${showDebugInfo ? 'Enabled' : 'Disabled'} - Show AdminStateDto debug information in AdminView`,
              async () => {
                await toggleDebugInfo();
                setLastOperation(`✅ Debug Info ${!showDebugInfo ? 'enabled' : 'disabled'}`);
              },
              showDebugInfo ? '✅' : '❌',
              false
            )}
            {renderButton(
              'Enable Edit Page Logic',
              `Current: ${enableEditPageLogic ? 'Enabled' : 'Disabled'} - Enable edit page logic in AdminView`,
              async () => {
                await toggleEditPageLogic();
                setLastOperation(`✅ Edit Page Logic ${!enableEditPageLogic ? 'enabled' : 'disabled'}`);
              },
              enableEditPageLogic ? '✅' : '❌',
              false
            )}
            {renderButton(
              'Enable Edit Calc Logic',
              `Current: ${enableEditCalcLogic ? 'Enabled' : 'Disabled'} - Enable edit calc logic in AdminView`,
              async () => {
                await toggleEditCalcLogic();
                setLastOperation(`✅ Edit Calc Logic ${!enableEditCalcLogic ? 'enabled' : 'disabled'}`);
              },
              enableEditCalcLogic ? '✅' : '❌',
              false
            )}
            {renderButton(
              'Enable Edit Report Logic',
              `Current: ${enableEditReportLogic ? 'Enabled' : 'Disabled'} - Enable edit report logic in ReportView`,
              async () => {
                await toggleEditReportLogic();
                setLastOperation(`✅ Edit Report Logic ${!enableEditReportLogic ? 'enabled' : 'disabled'}`);
              },
              enableEditReportLogic ? '✅' : '❌',
              false
            )}
            
            {renderButton(
              'Enable Premium Mode',
              `Current: ${debugPremiumMode === true ? 'Enabled' : 'Disabled'} - Override premium features for testing`,
              togglePremiumMode,
              debugPremiumMode === true ? '✅' : '❌',
              false
            )}
            {renderButton(
              'Clear All Admins',
              'Remove all AdminStateDto entries (in-progress assessment state) from storage. Response records and assessment definitions are preserved.',
              handleClearAllAdmins,
              '🗑️🎯',
              isLoading || !storage
            )}
          </>
        ))}

        {/* UI Features */}
        {renderSection('UI Features', (
          <>
            {renderButton(
              'Enable Change Account',
              `Current: ${enableChangeAccount ? 'Enabled' : 'Disabled'} - Allow changing account from My Account view`,
              async () => {
                const newValue = !enableChangeAccount;
                setEnableChangeAccount(newValue);
                
                // Persist the setting to storage
                const globalStorage = getRuntimeStorage();
                if (globalStorage) {
                  try {
                    await globalStorage.setItem('debug_enable_change_account', newValue.toString());
                    console.log(`🔧 Enable Change Account setting stored: ${newValue}`);
                  } catch (error) {
                    console.error('Failed to store Enable Change Account setting:', error);
                  }
                }
                
                console.log(`🔧 Enable Change Account changed to: ${newValue}`);
                setLastOperation(`✅ Change Account ${newValue ? 'enabled' : 'disabled'}`);
              },
              enableChangeAccount ? '✅' : '❌',
              false
            )}
            {renderButton(
              'Enable Last Update',
              `Current: ${enableLastUpdate ? 'Enabled' : 'Disabled'} - Show domain computation timestamps instead of staleness warnings`,
              async () => {
                const newValue = !enableLastUpdate;
                setEnableLastUpdate(newValue);
                
                // Persist the setting to storage
                const globalStorage = getRuntimeStorage();
                if (globalStorage) {
                  try {
                    await globalStorage.setItem('debug_show_last_update', newValue.toString());
                    console.log(`🔧 Enable Last Update setting stored: ${newValue}`);
                  } catch (error) {
                    console.error('Failed to store Enable Last Update setting:', error);
                  }
                }
                
                console.log(`🔧 Enable Last Update changed to: ${newValue}`);
                setLastOperation(`✅ Last Update Display ${newValue ? 'enabled' : 'disabled'}`);
              },
              enableLastUpdate ? '✅' : '❌',
              false
            )}
            {renderButton(
              'Settings: Push Notifications',
              `Current: ${enablePushNotifications ? 'Enabled' : 'Disabled'} - Show Push Notifications toggle in Settings > Preferences`,
              async () => {
                const newValue = !enablePushNotifications;
                setEnablePushNotifications(newValue);
                const globalStorage = getRuntimeStorage();
                if (globalStorage) {
                  try {
                    await globalStorage.setItem('debug_enable_push_notifications', newValue.toString());
                  } catch (error) {
                    console.error('Failed to store Push Notifications setting:', error);
                  }
                }
                console.log(`🔧 Settings Push Notifications changed to: ${newValue}`);
                setLastOperation(`✅ Settings Push Notifications ${newValue ? 'enabled' : 'disabled'}`);
              },
              enablePushNotifications ? '✅' : '❌',
              false
            )}
            {renderButton(
              'Settings: Dark Mode',
              `Current: ${enableDarkMode ? 'Enabled' : 'Disabled'} - Show Dark Mode toggle in Settings > Preferences`,
              async () => {
                const newValue = !enableDarkMode;
                setEnableDarkMode(newValue);
                const globalStorage = getRuntimeStorage();
                if (globalStorage) {
                  try {
                    await globalStorage.setItem('debug_enable_dark_mode', newValue.toString());
                  } catch (error) {
                    console.error('Failed to store Dark Mode setting:', error);
                  }
                }
                console.log(`🔧 Settings Dark Mode changed to: ${newValue}`);
                setLastOperation(`✅ Settings Dark Mode ${newValue ? 'enabled' : 'disabled'}`);
              },
              enableDarkMode ? '✅' : '❌',
              false
            )}
            {renderButton(
              'Settings: Auto Backup',
              `Current: ${enableAutoBackup ? 'Enabled' : 'Disabled'} - Show Auto Backup toggle in Settings > Preferences`,
              async () => {
                const newValue = !enableAutoBackup;
                setEnableAutoBackup(newValue);
                const globalStorage = getRuntimeStorage();
                if (globalStorage) {
                  try {
                    await globalStorage.setItem('debug_enable_auto_backup', newValue.toString());
                  } catch (error) {
                    console.error('Failed to store Auto Backup setting:', error);
                  }
                }
                console.log(`🔧 Settings Auto Backup changed to: ${newValue}`);
                setLastOperation(`✅ Settings Auto Backup ${newValue ? 'enabled' : 'disabled'}`);
              },
              enableAutoBackup ? '✅' : '❌',
              false
            )}
            {renderButton(
              'Settings: Analytics',
              `Current: ${enableAnalytics ? 'Enabled' : 'Disabled'} - Show Analytics toggle in Settings > Preferences`,
              async () => {
                const newValue = !enableAnalytics;
                setEnableAnalytics(newValue);
                const globalStorage = getRuntimeStorage();
                if (globalStorage) {
                  try {
                    await globalStorage.setItem('debug_enable_analytics', newValue.toString());
                  } catch (error) {
                    console.error('Failed to store Analytics setting:', error);
                  }
                }
                console.log(`🔧 Settings Analytics changed to: ${newValue}`);
                setLastOperation(`✅ Settings Analytics ${newValue ? 'enabled' : 'disabled'}`);
              },
              enableAnalytics ? '✅' : '❌',
              false
            )}
            {renderButton(
              'Dev Init Window',
              `Current: ${devInitWindow} milestones - Number of milestones below ceiling to set as floor`,
              () => {
                // For now, cycle through common values: 3, 4, 5, 6
                const nextValue = devInitWindow >= 6 ? 3 : devInitWindow + 1;
                setDevInitWindow(nextValue);
                // In production, this would update the persistent config
                console.log(`🔧 Dev Init Window changed to: ${nextValue}`);
                setLastOperation(`✅ Dev Init Window set to ${nextValue} milestones`);
              },
              '🔢',
              false
            )}
            {renderButton(
              'Yes Required',
              `Current: ${devYesRequired} milestones - Number of 'yes' responses required to establish floor`,
              () => {
                // For now, cycle through common values: 1, 2, 3, 4
                const nextValue = devYesRequired >= 4 ? 1 : devYesRequired + 1;
                setDevYesRequired(nextValue);
                console.log(`🔧 Yes Required changed to: ${nextValue}`);
                setLastOperation(`✅ Yes Required set to ${nextValue} milestones`);
              },
              '✅',
              false
            )}
            {renderButton(
              'Not Yet Required',
              `Current: ${devNotYetRequired} milestones - Number of 'not-yet' responses required to establish ceiling`,
              () => {
                // For now, cycle through common values: 1, 2, 3, 4
                const nextValue = devNotYetRequired >= 4 ? 1 : devNotYetRequired + 1;
                setDevNotYetRequired(nextValue);
                console.log(`🔧 Not Yet Required changed to: ${nextValue}`);
                setLastOperation(`✅ Not Yet Required set to ${nextValue} milestones`);
              },
              '🔢',
              false
            )}
          </>
        ))}

        {/* Content Bundling & Deployment */}
        {renderSection('Content Bundling & Deployment', (
          <>
            {renderInfoItem('Bundle Config', `${REQUIRED_CONTENT_CONFIG.adminSegments.length} AdminSegments + ${REQUIRED_CONTENT_CONFIG.otherContent.length} other items`)}
            {renderInfoItem('Bundle Version', REQUIRED_CONTENT_CONFIG.bundleInfo.version)}
            
            {/* Bundle Configuration Management */}
            {renderButton(
              'Update Bundle Config from Store',
              'Replace bundle config with current Content Store entries',
              handleUpdateBundleConfigFromStore,
              '📋🔄',
              isLoading || !useContentStore || !storage
            )}
            {renderButton(
              'View Bundle Config Details',
              'Show detailed list of items in bundle configuration',
              handleViewBundleConfigDetails,
              '📋👀',
              false
            )}
            {renderButton(
              'Reset Bundle Config',
              'Reset bundle configuration to default items',
              handleResetBundleConfig,
              '📋🔄',
              isLoading || !storage
            )}
            
            {/* Bundle Operations */}
            {renderButton(
              'Generate Content Bundle',
              'Create deployable bundle from current content',
              handleGenerateContentBundle,
              '📦',
              isLoading || !useContentStore || !storage
            )}
            {renderButton(
              'Test Bundled Content',
              'Test loading content from bundled assets',
              handleTestBundledContent,
              '🧪',
              isLoading || !storage || !useContentStore
            )}
            
            {/* Content Management Operations */}
          </>
        ))}

        {/* Custom Download Section */}
        {renderSection('Custom Download', (
          <>
            <View style={styles.inputGroup}>
              <Text style={styles.inputLabel}>Content Type</Text>
              <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8 }}>
                {contentTypeOptions.map((option) => (
                  <TouchableOpacity
                    key={option.value}
                    style={[
                      styles.dropdown,
                      { flex: 1, justifyContent: 'center' },
                      customContentType === option.value && { backgroundColor: '#3498db', borderColor: '#2980b9' }
                    ]}
                    onPress={() => {
                      console.log('🔎 DebugView Custom Download selected content type:', option.value);
                      setCustomContentType(option.value);
                    }}
                  >
                    <Text style={[styles.dropdownText, customContentType === option.value && { color: '#fff', fontWeight: '600' }]}>
                      {option.label}
                    </Text>
                  </TouchableOpacity>
                ))}
              </View>
            </View>
            <View style={styles.inputGroup}>
              <Text style={styles.inputLabel}>Content ID</Text>
              <TextInput
                style={styles.input}
                value={customContentId}
                onChangeText={setCustomContentId}
                placeholder="1987 (AdminSegment) or assessment ID"
              />
            </View>
            <View style={styles.inputGroup}>
              <Text style={styles.inputLabel}>Language ID</Text>
              <TextInput
                style={styles.input}
                value={customLanguageId}
                onChangeText={setCustomLanguageId}
                placeholder="1"
              />
            </View>
            {renderButton(
              'Download Content',
              `Download ${customContentType} with specified parameters`,
              handleDownloadContent,
              '⬇️',
              isLoading
            )}
          </>
        ))}

        {/* Content List Section */}
        {renderSection('Content Store Entries', (
          <>
            {renderButton(
              'Clear All Content',
              'Remove all downloaded content from local store',
              handleClearAllContent,
              '🗑️',
              isLoading || !useContentStore
            )}
            {contentEntries.length === 0 ? (
              <View style={styles.emptyState}>
                <Text style={styles.emptyStateText}>No content entries found</Text>
                <Text style={styles.emptyStateSubtext}>Download some content to see it listed here</Text>
              </View>
            ) : (
              contentEntries.map((entry, index) => renderContentEntry(entry, index))
            )}
          </>
        ))}

        {/* Assessment Definitions Section */}
        {renderSection('Assessment Definitions (admin_assessment_list)', (
          <>
            {renderInfoItem('ℹ️ Note', 'These are assessment type DEFINITIONS (admin_assessment_list) — not completed results. See the Completed Assessment Responses section below for the same data shown in WellVisitView > History.')}
            {renderButton(
              'Refresh Assessments',
              'Reload assessment entries from storage',
              loadAssessmentEntries,
              '🔄',
              isLoading || !storage
            )}
            {renderButton(
              'Clear All Assessments',
              'Remove all assessment definitions from storage',
              handleClearAllAssessments,
              '🗑️',
              isLoading || !storage
            )}
            {assessmentEntries.length === 0 ? (
              <View style={styles.emptyState}>
                <Text style={styles.emptyStateText}>No assessment entries found</Text>
                <Text style={styles.emptyStateSubtext}>Download some assessments to see them listed here</Text>
              </View>
            ) : (
              assessmentEntries.map((entry, index) => (
                <View key={index} style={styles.contentEntry}>
                  <View style={styles.contentInfo}>
                    <Text style={styles.contentId}>{entry.dName}</Text>
                    <Text style={styles.contentDetails}>
                      Source ID: {entry.sourceId} | Surveyb List: {entry.surveybList}
                      {'\n'}Type: {entry.sourceType} | Language: {entry.languageTypeId}
                      {'\n'}Reports: {entry.reportsCount}
                    </Text>
                  </View>
                </View>
              ))
            )}
          </>
        ))}

        {/* Report Script Tools — Segment Label Generator */}
        {renderSection('Report Script Tools', (
          <>
            <View style={styles.infoContainer}>
              <Text style={styles.infoText}>
                Generates a hardcoded JS object from a downloaded AdminSegment so you can paste it directly into a report script.{'\n'}
                {'  '}• Regular questions: mvarName → question text{'\n'}
                {'  '}• Multi-select (MvarTypeId=2, e.g. segment 11272): mvarName_keyValue → answer text{'\n'}
                The segment must already be downloaded before generating.
              </Text>
            </View>
            <View style={styles.inputGroup}>
              <Text style={styles.inputLabel}>Segment ContentTypeId</Text>
              <TextInput
                style={styles.input}
                value={segLabelSegmentId}
                onChangeText={setSegLabelSegmentId}
                placeholder="e.g. 11272"
                keyboardType="numeric"
              />
            </View>
            <View style={styles.inputGroup}>
              <Text style={styles.inputLabel}>Variable Name</Text>
              <TextInput
                style={styles.input}
                value={segLabelVarName}
                onChangeText={setSegLabelVarName}
                placeholder="e.g. concernLabels"
                autoCapitalize="none"
              />
            </View>
            {renderButton(
              'Generate Labels JSON',
              'Walk the segment questions/answers and produce a const object literal',
              handleGenerateSegmentLabels,
              '🏷️',
              isGeneratingLabels || !contentService
            )}
            {segLabelOutput ? (
              <View style={styles.inputGroup}>
                <Text style={styles.inputLabel}>Output — select all and copy:</Text>
                <TextInput
                  style={[styles.input, { minHeight: 160, fontFamily: 'monospace', fontSize: 12, textAlignVertical: 'top' }]}
                  value={segLabelOutput}
                  multiline
                  editable
                  selectTextOnFocus
                />
              </View>
            ) : null}
            {renderButton(
              'Generate Node Order JSON',
              'Emit the AdminNodeDto.Order array for each node as a const object',
              handleGenerateNodeOrder,
              '🔢',
              isGeneratingNodeOrder || !contentService
            )}
            {segNodeOrderOutput ? (
              <View style={styles.inputGroup}>
                <Text style={styles.inputLabel}>Node Order Output — select all and copy:</Text>
                <TextInput
                  style={[styles.input, { minHeight: 160, fontFamily: 'monospace', fontSize: 12, textAlignVertical: 'top' }]}
                  value={segNodeOrderOutput}
                  multiline
                  editable
                  selectTextOnFocus
                />
              </View>
            ) : null}
            {renderButton(
              'Generate Answer Labels JSON',
              'Produce a const object mapping mvarId → { dataValue: answerText } for all answers',
              handleGenerateAnswerLabels,
              '📋',
              isGeneratingAnswerLabels || !contentService
            )}
            {segAnswerLabelsOutput ? (
              <View style={styles.inputGroup}>
                <Text style={styles.inputLabel}>Answer Labels Output — select all and copy:</Text>
                <TextInput
                  style={[styles.input, { minHeight: 160, fontFamily: 'monospace', fontSize: 12, textAlignVertical: 'top' }]}
                  value={segAnswerLabelsOutput}
                  multiline
                  editable
                  selectTextOnFocus
                />
              </View>
            ) : null}
          </>
        ))}

        {/* Completed Assessment Responses Section — mirrors WellVisitView > History */}
        {renderSection('Completed Assessment Responses (admin_responses_<profileId>)', (
          <>
            {renderInfoItem('ℹ️ Note', 'These are completed assessment RESULTS stored per-profile — the same data shown in WellVisitView > History tab.')}
            {renderButton(
              'Refresh Responses',
              'Reload completed assessment responses for the active profile',
              loadAssessmentResponses,
              '🔄',
              isLoading || !storage
            )}
            {renderButton(
              'Clear Responses',
              'Remove all local AdminResponseDto records (completed assessment history) for all profiles',
              handleClearAllResponses,
              '🗑️',
              isLoading || !storage
            )}
            {assessmentResponses.length === 0 ? (
              <View style={styles.emptyState}>
                <Text style={styles.emptyStateText}>No completed assessments found</Text>
                <Text style={styles.emptyStateSubtext}>Complete an assessment in WellVisitView to see results here</Text>
              </View>
            ) : (
              assessmentResponses.map((resp, index) => (
                <View key={index} style={styles.contentEntry}>
                  <View style={styles.contentInfo}>
                    <Text style={styles.contentId}>{resp.recAlias || resp.recSurvey || `Response ${index + 1}`}</Text>
                    <Text style={styles.contentDetails}>
                      RecId: {resp.recId || '(pending)'} | Survey: {resp.recSurvey}
                      {'\n'}Date: {resp.recDate ? new Date(resp.recDate).toLocaleString() : 'n/a'}
                      {'\n'}Status: {resp.recStatus || 'n/a'} | Responses: {resp.responseData?.length ?? 0}
                    </Text>
                  </View>
                </View>
              ))
            )}
          </>
        ))}

      </ScrollView>
      
      {/* Status Display */}
      {(lastOperation || isLoading) && (
        <View style={styles.statusContainer}>
          <Text style={styles.statusText}>
            {isLoading ? '⏳ Processing...' : lastOperation}
          </Text>
        </View>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#f8f9fa',
  },
  header: {
    backgroundColor: '#ffffff',
    paddingHorizontal: 16,
    paddingTop: 16,
    paddingBottom: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#e1e8ed',
    flexDirection: 'row',
    alignItems: 'center',
  },
  backButton: {
    paddingVertical: 8,
    paddingHorizontal: 12,
  },
  backButtonText: {
    fontSize: 16,
    color: '#3498db',
    fontWeight: '600',
  },
  titleContainer: {
    flex: 1,
    alignItems: 'center',
  },
  title: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#2c3e50',
  },
  subtitle: {
    fontSize: 14,
    color: '#7f8c8d',
    marginTop: 2,
  },
  content: {
    flex: 1,
  },
  section: {
    backgroundColor: '#ffffff',
    marginTop: 12,
    paddingVertical: 16,
    borderTopWidth: 1,
    borderBottomWidth: 1,
    borderColor: '#e1e8ed',
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#2c3e50',
    paddingHorizontal: 20,
    marginBottom: 12,
  },
  button: {
    paddingHorizontal: 20,
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#f1f2f6',
  },
  buttonDisabled: {
    opacity: 0.5,
  },
  buttonContent: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  buttonIcon: {
    fontSize: 18,
    marginRight: 12,
  },
  buttonText: {
    flex: 1,
  },
  buttonTitle: {
    fontSize: 16,
    fontWeight: '500',
    color: '#2c3e50',
    marginBottom: 2,
  },
  buttonTitleDisabled: {
    color: '#7f8c8d',
  },
  buttonDescription: {
    fontSize: 14,
    color: '#7f8c8d',
  },
  buttonDescriptionDisabled: {
    color: '#bdc3c7',
  },
  infoItem: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 20,
    paddingVertical: 8,
    borderBottomWidth: 1,
    borderBottomColor: '#f1f2f6',
  },
  infoLabel: {
    fontSize: 14,
    color: '#7f8c8d',
    flex: 1,
  },
  infoValue: {
    fontSize: 14,
    color: '#2c3e50',
    fontWeight: '500',
    flex: 2,
    textAlign: 'right',
  },
  inputGroup: {
    paddingHorizontal: 20,
    paddingVertical: 8,
  },
  inputLabel: {
    fontSize: 14,
    color: '#2c3e50',
    fontWeight: '500',
    marginBottom: 4,
  },
  input: {
    backgroundColor: '#f8f9fa',
    borderWidth: 1,
    borderColor: '#e1e8ed',
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 8,
    fontSize: 14,
    color: '#2c3e50',
  },
  dropdown: {
    backgroundColor: '#f8f9fa',
    borderWidth: 1,
    borderColor: '#e1e8ed',
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 8,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  dropdownText: {
    fontSize: 14,
    color: '#2c3e50',
  },
  dropdownArrow: {
    fontSize: 12,
    color: '#7f8c8d',
  },
  contentEntry: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 20,
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#f1f2f6',
  },
  contentInfo: {
    flex: 1,
  },
  contentType: {
    fontSize: 14,
    fontWeight: '600',
    color: '#2c3e50',
  },
  contentId: {
    fontSize: 12,
    color: '#7f8c8d',
    marginTop: 2,
  },
  contentDetails: {
    fontSize: 12,
    color: '#95a5a6',
    marginTop: 4,
    lineHeight: 16,
  },
  contentLang: {
    fontSize: 12,
    color: '#7f8c8d',
  },
  contentDate: {
    fontSize: 12,
    color: '#7f8c8d',
  },
  deleteButton: {
    padding: 8,
  },
  deleteButtonText: {
    fontSize: 16,
  },
  emptyState: {
    alignItems: 'center',
    paddingVertical: 32,
    paddingHorizontal: 20,
  },
  emptyStateText: {
    fontSize: 16,
    color: '#7f8c8d',
    fontWeight: '500',
  },
  emptyStateSubtext: {
    fontSize: 14,
    color: '#bdc3c7',
    marginTop: 4,
    textAlign: 'center',
  },
  infoContainer: {
    backgroundColor: '#f8f9fa',
    padding: 12,
    borderRadius: 8,
    marginTop: 8,
    borderWidth: 1,
    borderColor: '#e9ecef',
  },
  infoText: {
    fontSize: 13,
    color: '#495057',
    lineHeight: 18,
  },
  debugSection: {
    backgroundColor: '#f8f9fa',
    padding: 12,
    marginHorizontal: 20,
    marginVertical: 8,
    borderRadius: 8,
    borderLeftWidth: 4,
    borderLeftColor: '#3b82f6',
  },
  debugSectionTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: '#2c3e50',
    marginBottom: 8,
  },
  debugText: {
    fontSize: 12,
    color: '#5a6c7d',
    marginBottom: 4,
    fontFamily: 'monospace',
  },
  statusContainer: {
    backgroundColor: '#ffffff',
    padding: 12,
    borderTopWidth: 1,
    borderTopColor: '#e1e8ed',
  },
  statusText: {
    fontSize: 14,
    color: '#2c3e50',
    textAlign: 'center',
  },
});